<template>
  <div :id="'qryContent_' + queryID"
       :class="{'embedded q-px-md q-pt-none q-pb-none' : embedded && !popupMode,
                'q-pt-md q-px-md q-px-xs-sm relative-position' : !embedded && !popupMode,
                'q-px-none' : popupMode,
                'query-dialog-scroll' : dialogScroll,
                'cardContainer' : viewMode === StaticData.VIEW_MODES_QUERY.CARD}"
       :style="dialogScroll ? 'height:100%;min-height:0' : dashboardHeaderProp && dashboardMaximized ? 'height: calc(100vh - 170px)' : dashboardMaximized ? 'height: calc(100vh - 60px)' : embedded ? 'height:100%' : 'min-height:calc(100vh - 60px)'"
  >
    <div :id="'qryHead_' + queryID" class="row qryHead">
      <div v-if="!suppressTitle" class="col">
        <h3 v-if="inAppMode" style="font-size:25px;font-weight:bold;margin-top:3px">{{ resolvedTitle }}</h3>
        <h3 v-else-if="!detailQueryKeyValue">{{ resolvedTitle }}</h3>
        <h3 v-else style="font-size:20px">{{ resolvedTitle }}</h3>
        <p v-if="!embedded && queryMeta.info!==''" class="text-grey-8 q-mb-none q-mt-xs" v-html="resolvedInfo"></p>
      </div>
      <!-- Keep the same controls and bindings; only direct result dialogs provide a header target. -->
      <Teleport :to="dialogHeaderTarget || 'body'" :disabled="!dialogHeaderTarget">
      <div class="col-auto">
        <!-- Embedded param, result, chart, plugin toggler -->
        <q-btn-toggle
          v-model="selTab"
          v-if="!suppressViewModeToggle && (embedded && (getEmbeddedViewModetoggleOptions().length>1)) && queryMeta.navToolbarVisible"
          class="embedViewMode q-ml-sm"
          size="xs"
          unelevated
          no-caps
          dense
          toggle-color="primary"
          text-color="primary"
          :options="getEmbeddedViewModetoggleOptions()"
        >
          <template #parameter v-if="showParameterPanel">
            <div class="row items-center">
              <q-icon name="view_list" class="q-ml-xs q-mr-sm"/>
              <q-tooltip>{{ $t('queryTabParameter') }}</q-tooltip>
            </div>
          </template>
          <template #result :disable="!queryExecuteDone">
            <div class="row items-center">
              <q-icon name="grid_on" class="q-ml-xs q-mr-sm"/>
              <q-tooltip>{{ $t('queryTabResult') }}</q-tooltip>
            </div>
          </template>
          <template #chart v-if="showChartPanel">
            <div class="row items-center">
              <q-space/>
              <q-icon name="bar_chart" class="q-ml-xs q-mr-sm"/>
              <q-tooltip>{{ $t('queryTabChart') }}</q-tooltip>
            </div>
          </template>
          <template #plugin v-if="showPluginPanel">
            <div class="row items-center">
              <q-space/>
              <q-icon name="mdi-puzzle" class="q-ml-xs q-mr-sm"/>
              <q-tooltip>{{ $t('queryTabPlugin') }}</q-tooltip>
            </div>
          </template>
        </q-btn-toggle>

        <!-- Search desktop -->
        <q-input class="gt-sm float-left" dense debounce="1000" v-model="filter.globalSearch" :label="$t('querySearch')" v-if="queryMeta.resultsetAllowsearch" filled borderless :style="inAppMode?'width:150px':'width:180px'" clearable
                 @clear="filter.globalSearch = ''">
          <template #append>
            <q-icon name="search"/>
          </template>
        </q-input>

        <!-- Search mobile -->
        <q-btn flat round dense size="md" icon="search" class="lt-md float-left" v-if="queryMeta.resultsetAllowsearch" @click="showMobileSearch=true"/>
        <q-dialog v-model="showMobileSearch" position="top"> <!-- :persistent="$q.platform.is.safari" -->
          <q-card>
            <q-card-section class="row items-center q-pb-none">
              <div class="text-h6">{{ $t('querySearch') }}</div>
              <q-space/>
              <q-btn icon="close" flat round dense v-close-popup/>
            </q-card-section>
            <q-card-section class="row items-center no-wrap">
              <q-input class="txtMobileFilter" autofocus seamless position="top" dense debounce="1000" v-model="filter.globalSearch" filled borderless @keyup.enter="showMobileSearch=false" clearable @clear="filter.globalSearch = ''">
                <template #append>
                  <q-icon name="search"/>
                </template>
              </q-input>
            </q-card-section>
          </q-card>
        </q-dialog>

        <!-- Autorefresh -->
        <q-circular-progress v-if="queryMeta.autorefresh && !singleRecViewEdit && doAutorefresh" :value="autorefreshTime" size="25px" :thickness="0.2" color="primary" track-color="grey-12" class="q-mt-sm q-ml-sm float-left" id="refreshProgress"/>
        <q-btn flat dense size="xs" v-if="queryMeta.autorefresh && !singleRecViewEdit && doAutorefresh" color="primary" class="float-left" style="margin:11px 5px 0 -22px"
               @click="btnStopAutorefresh ? btnStopAutorefresh=false : btnStopAutorefresh=true">
          <q-icon :name="btnStopAutorefresh ? 'play_arrow' : 'stop'" style="font-size: 14px"/>
          <q-tooltip v-if="btnStopAutorefresh">{{ $t('restartAutorefresh') }}</q-tooltip>
          <q-tooltip v-else="btnStopAutorefresh">{{ $t('stopAutorefresh') }}</q-tooltip>
        </q-btn>

        <!-- Fullscreen -->
        <q-btn flat round dense size="md" class="float-left q-mt-xs" v-if="queryMeta.resultsetFullscreen && !embedded" :icon="inFullscreen ? 'fullscreen_exit' : 'fullscreen'"
               @click="inFullscreen ? inFullscreen=false : inFullscreen=true;calcContentHeight()">
          <q-tooltip>{{ $t('fullscreen') }}</q-tooltip>
        </q-btn>
        <q-btn v-if="(embedded || dashboardMaximized) && queryMeta.resultsetFullscreen" flat dense @click="$emit('onfullscreen'); dashboardMaximized = !dashboardMaximized; embedded = !embedded"
               :icon="dashboardMaximized ? 'unfold_less' : 'unfold_more'" class="q-mt-xs" style="margin-top:7px;margin-left:4px">
          <q-tooltip>{{ $t('fullscreenOnOff') }}</q-tooltip>
        </q-btn>

        <!-- Chart Zoom Reset -->
        <!--
                <q-btn v-if="showChartPanel && selTab == QUERY_TABS.CHART" flat dense size="md" class="q-mt-xs" icon="zoom_out" @click="chartZoomResetKey++" style="margin-top:7px">
                  <q-tooltip>{{ $t('resetChartZoom') }}</q-tooltip>
                </q-btn>
        -->

        <!-- ColumnDrop -->
        <q-select v-if="queryMeta.allowColumnDrop && !dashboardMaximized" v-model="visibleColumns" multiple borderless flat round dense options-dense display-value="" hide-dropdown-icon class="q-mr-none float-left"
                  emit-value map-options :options="availableColumns" option-value="name" @update:model-value="localStoreInvisibleColumns()">
          <template #prepend>
            <q-icon name="more_vert"/>
          </template>
          <q-tooltip>{{ $t('enableDisableRows') }}</q-tooltip>
        </q-select>
      </div>
      </Teleport>
    </div>

    <!-- v-show="!queryMeta.skipParamsOnDefault || (queryMeta.skipParamsOnDefault && queryExecuteDone)" -->
    <div :id="'qryToolbar_' + queryID" :class="stickyCSS" v-show="!queryMeta.skipParamsOnDefault || (queryMeta.skipParamsOnDefault && queryExecuteDone)">
      <q-toolbar class="q-pa-none" v-if="!embedded && !inFullscreen && queryMeta.navToolbarVisible">
        <q-tabs v-model="selTab" dense class="text-grey q-pa-none" active-color="primary" indicator-color="primary" align="left" narrow-indicator :breakpoint="0">
          <q-tab :name="QUERY_TABS.PARAMETER" label="PARAM" v-if="showParameterPanel" class="q-px-xs-sm q-px-sm-md xs"/>
          <q-tab :name="QUERY_TABS.PARAMETER" :label="$t('queryTabParameter')" v-if="showParameterPanel" class="q-px-xs-sm q-px-sm-md xs-hide"/>
          <q-tab :name="QUERY_TABS.RESULT" label="TAB" :disable="!queryExecuteDone" class="q-px-xs-sm q-px-sm-md xs"/>
          <q-tab :name="QUERY_TABS.RESULT" :label="$t('queryTabResult')" :disable="!queryExecuteDone" class="q-px-xs-sm q-px-sm-md xs-hide" data-test="resultQuery"/>
          <q-tab :name="QUERY_TABS.DETAIL" :label="$t('queryTabDetail')" v-if="queryMeta.resultsetShowsinglerecord && queryResult && queryResult.length > 0" :disable="selected.length === 0" class="q-px-xs-sm q-px-sm-md" data-test="viewQuery"/>
          <q-tab :name="QUERY_TABS.EDIT" :label="$t('queryTabEdit')" v-if="isUpdateable" :disable="selected.length==0" class="q-px-xs-sm q-px-sm-md" data-test="editQuery"/>
          <q-tab :name="QUERY_TABS.CHART" :label="$t('queryTabChart')" v-if="showChartPanel" class="q-px-xs-sm q-px-sm-md"/>
          <q-tab :name="QUERY_TABS.PLUGIN" :label="$t('queryTabPlugin')" v-if="showPluginPanel" class="q-px-xs-sm q-px-sm-md"/>
          <q-tab :name="QUERY_TABS.WEBENTITY" :label="$t('queryTabWebentity')" v-if="showWebentity" class="q-px-xs-sm q-px-sm-md"/>
        </q-tabs>
      </q-toolbar>

      <q-separator v-if="!embedded && !inFullscreen && queryMeta.navToolbarVisible"/>

      <!-- Action buttons -->
      <div class="q-gutter-sm actions" v-if="selTab!==QUERY_TABS.PARAMETER && showActions && !inFullscreen && getVisibleActions().length>0">
        <!------------------ BEGIN ACTIONS --------------------->
        <!-- MOBILE ACTIONS: actions 'group' name ignored : render as btn-dropdown when more then one action, else render as one button -->
        <template v-if="$q.screen.name==='xs'">
          <q-btn-dropdown color="primary" outline label="Aktionen" unelevated icon="flash_on" v-if="getVisibleActions().length>4" class="btnNarrow">
            <q-list separator :dense="getVisibleActions().length>5">
              <q-item :clickable="!action.contextrequired || (action.contextrequired && selected.length>0)"
                      :disable="getDisabledActions(selected, action)" v-close-popup v-ripple v-for="action in getVisibleActions()" :key="action.id"
                      @click="btnDoQryAction(action, true)">
                <q-item-section side top>
                  <q-img v-if="action.iconClass && action.iconURL.indexOf('ico-flash')===-1" :src="getIconURL(action)" style="width:24px"/>
                  <q-icon v-else-if="action.iconClass" :name="action.iconClass"/>
                  <q-icon v-else name="flash_on"/>
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ action.title }}</q-item-label>
                  <q-item-label caption lines="2">{{ action.tooltipMsg }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-btn-dropdown>

          <q-btn v-else v-for="action in getVisibleActions()" :key="action.id"
                 :outline="!action.cssClass & !action.cssStyle ? true : false"
                 :color="getActionBtnColor(action)"
                 :style=action.cssStyle :class="'btnNarrow ' + action.cssClass + (action.blink?' blink':'')"
                 :disabled="getDisabledActions(selected, action)"
                 @click="btnDoQryAction(action, true)"
                 :data-test="'resultSetAction' + action.title">
            <q-avatar size="20px">
              <q-img v-if="action.iconClass && action.iconURL.indexOf('ico-flash')==-1" :src="getIconURL(action)" style="width:24px"/>
              <q-icon v-else-if="action.iconClass" :name="action.iconClass"/>
              <q-icon v-else name="flash_on"/>
            </q-avatar>
            <div style="margin-left:10px">
              {{ action.title }}
            </div>
            <q-tooltip v-if="action.tooltipMsg!==''">{{ action.tooltipMsg }}</q-tooltip>
          </q-btn>
        </template>

        <!-- DESKTOP ACTIONS: grouped actions with 'group' name : grouped in btn-dropdown -->
        <template v-else>
          <template v-if="queryExecuteDone && resetActionGroups()">
            <template v-for="action in getVisibleActions()" :key="action.id">
              <!-- desktop: without 'group' name : default q-btn -->
              <q-btn v-if="action.actiongroup === undefined || action.actiongroup == null || action.actiongroup == ''" act
                     :outline="!action.cssClass & !action.cssStyle ? true : false"
                     :color="getActionBtnColor(action)"
                     :id="action.nativename" unelevated
                     :class="'btnNarrow ' + action.cssClass + (action.blink?' blink':'')" :style=action.cssStyle :disabled="getDisabledActions(selected, action)"
                     @click="btnDoQryAction(action, true)" :data-test="'resultSetAction' + action.title">
                <q-avatar size="20px" square>
                  <q-img v-if="action.iconClass && action.iconURL.indexOf('ico-flash')==-1" :src="getIconURL(action)" style="width:24px"/>
                  <q-icon v-else-if="action.iconClass" :name="action.iconClass"/>
                  <q-icon v-else name="flash_on"/>
                </q-avatar>
                <div style="margin-left:5px">
                  {{ action.title }}
                </div>
                <q-tooltip v-if="action.tooltipMsg!=''">{{ action.tooltipMsg }}</q-tooltip>
              </q-btn>
              <q-btn-dropdown v-else v-show="renderActionGroup(action.actiongroup)" :color="getActionGroupColor(action)" class="btnNarrow" :label="action.actiongroup" unelevated outline :icon="getActionGroupIcon(action)">
                <q-list separator :dense="queryActionList.length>5" v-for="ac in getActionsInSameGroup(action)" :key="ac.id">
                  <q-item :clickable="!ac.contextrequired || (ac.contextrequired && selected.length>0)"
                          :disable="getDisabledActions(selected, ac)" v-close-popup v-ripple @click="btnDoQryAction(ac, true)">
                    <q-item-section side top>
                      <q-img v-if="ac.iconClass && ac.iconURL.indexOf('ico-flash')==-1" :src="getIconURL(ac)" style="width:24px"/>
                      <q-icon v-else-if="ac.iconClass" :name="ac.iconClass"/>
                      <q-icon v-else name="flash_on"/>
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>{{ ac.title }}</q-item-label>
                      <q-item-label caption lines="2">{{ ac.tooltipMsg }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </template>
          </template>
        </template>
      </div>
      <!-------------------- END ACTIONS ----------------------->
    </div>

    <!-- v-show="!queryMeta.skipParamsOnDefault || (queryMeta.skipParamsOnDefault && queryExecuteDone)" -->
    <q-tab-panels v-model="selTab" keep-alive @transition="selTab===QUERY_TABS.RESULT?restoreScrollPosition():''" id="queryTable" :animated="false" class="bg-transparent">
      <q-tab-panel :name="QUERY_TABS.PARAMETER" class="q-pl-none q-pr-none" v-if="showParameterPanel">
        <Parameter :queryMeta="queryMeta" :queryParameter="queryParameter" @parameterEntered="parameterEntered" :autoSkipParameter="autoSkipParameter"
                   :key="queryID + queryParameterRefreshCounter"
                   v-show="(!queryMeta.skipParamsOnDefault || (queryMeta.skipParamsOnDefault && queryExecuteDone)) && !autoSkipParameter"/>
      </q-tab-panel>

      <q-tab-panel :name="QUERY_TABS.RESULT" class="q-pa-none no-scroll">
        <template v-if="queryResult[0]?Object.keys(queryResult[0])[0]!=='SINGLE_COL_RESULT':true" style="position: relative">
          <Parameter :id="'volatileParamBar_' + queryID" v-if="showParameterPanel" :queryMeta="queryMeta" :queryParameter="queryParameter" :appEmbedded="inAppMode"
                     @parameterEntered="parameterEntered" :volatile="true" :key="queryID + queryParameterRefreshCounter"/>

          <q-table
            ref="queryTable"
            tabindex="0"
            v-if="queryExecuteDone && resetGroups()"
            :visible-columns="getVisibleColumnsForTable()"
            :row-key="rowKey"
            v-model:pagination="paginationControl"
            :grid="isViewModeCard()"
            :rows-per-page-options="rowsPerPageOptions"
            :rows="queryResult"
            :columns="sortedColumns"
            v-model:selected="selected"
            :selection="queryMeta.resultsetMultiselect ? 'multiple' : undefined"
            separator="horizontal" class="my-sticky-header-table" :style="{ height: contentHeight + 'px', visibility: scrollingToSelection || (inDashboard && !tableHeightReady) ? 'hidden' : 'visible' }" dense flat hide-selected-banner
            :filter="filter" :filter-method="myFilter"
            :sort-method="customSort"
            @update:pagination="tableDrawnCallback"
            @focusin="navigationActive = true"
            @focusout="navigationActive = false"
          > <!-- :class="$q.platform.is.mobile?'':'my-sticky-header-table'" :style="$q.platform.is.mobile?'':{ height: contentHeight + 'px' }" -->

            <!-------------------- SEARCH, column drop ----------------->
            <!----------------------- Column header -------------------->
            <template #header="props">
              <draggable
                v-if="resetGroups() && queryMeta.renderModeID !== StaticData.QUERY_RENDERMODE.EAPPLICATION_RENDERER"
                :disabled="!queryMeta.allowColumnReorder"
                :list="sortedMultiselectColumns"
                item-key="name"
                ghost-class="drag-ghost"
                tag="tr"
                :component-data="{
                  tag: 'div',
                  type: 'transition-group',
                  name: 'table-header-drag',
                  animation: 200
                }"
                @change="onDragTableHeader($event.moved)"
              >
                <template #item="{element}">
                  <q-th auto-width v-if="element.multiselect">
                    <q-checkbox v-model="props.selected" dense/>
                  </q-th>
                  <q-th v-else :props="props" :style="'width:' + element.width + '; max-width:' + element.width+ ''">
                    {{ element.label }}
                    <q-icon v-if="element.projectionSmartCol" name="filter_alt" @click.stop>
                      <q-popup-proxy>
                        <q-input v-model="filter[element.name]" dense filled debounce="1000" style="margin: 4px; font-size: 14px" placeholder="Suchbegriff" clearable @clear="filter[element.name] = ''"/>
                        <q-select v-model="filter[element.name]" :options="filterArrays[element.name]" dense options-dense filled style="margin: 4px" label="Gruppierung"/>
                      </q-popup-proxy>
                    </q-icon>
                    <span v-if="element.projectionSmartCol && filter[element.name] && filter[element.name] != ''">*</span>
                  </q-th>
                </template>
              </draggable>
              <q-tr v-else style="display:none">
                <q-th></q-th>
              </q-tr>
            </template>

            <!------------------------- TABLE BODY ---------------------->
            <template #body="props">
              <!-- groupheader row -->
              <q-tr v-if="queryMeta.renderModeID && groupedView && renderGroup(props.row[groupColName])" class="cssRow" @click="unshowGroup(props.row[groupColName])" :id="props.row[rowKey]">
                <q-td colspan="100%" class="bg-grey-4">
                  <span v-html="props.row[groupColName]" class="text-bold"></span>
                  <q-icon :name="showGroup(props.row[groupColName]) ? 'keyboard_arrow_down' : 'keyboard_arrow_up'"></q-icon>
                </q-td>
              </q-tr>

              <!-- default row -->
              <q-tr
                v-if="queryMeta.renderModeID!==StaticData.QUERY_RENDERMODE.EAPPLICATION_RENDERER"
                @mouseover.native="hoveredRow=props.row"
                @dblclick.native="dblClickRow(props)"
                @click.exact="toggleSelectedRow(props.row, $event);btnDoAPPModeAction(0, true)"
                @click.ctrl.exact="toggleSelectedRowCTRL(props.row)"
                @click.meta.exact="toggleSelectedRowCTRL(props.row)"
                @click.shift.exact="toggleSelectedRowSHIFT(props.row)"
                @contextmenu.prevent.stop="showContextMenu($event, props.row)"
                v-show="showGroup(props.row[groupColName])"
                :class="{
                  'cssRow': true,
                  'secLineNoBorder': secondLineColumns.length>0,
                  'selected': selected.includes(props.row)
                }">
                <q-td auto-width v-if="queryMeta.resultsetMultiselect" style="vertical-align: middle">
                  <q-checkbox v-model="props.selected" dense @update:model-value="toggleSelectedRowCheckbox($event, props.row)"/>
                </q-td>
                <q-td v-for="(col, idx) in props.cols.filter(c => c.width!=='0px')" :key="col.name" :class="getProjCSS(col, props.row)"
                      :style="{
                    width: col.width,
                    maxWidth: col.width,
                    verticalAlign:'top',
                    textAlign: col.align
                  }">
                  <template v-if="col.projectionMicrochartType">
                    <q-linear-progress v-if="col.projectionMicrochartType === 1 && isNumber(props.row[col.name]/props.row[col.projectionMicrochartFullValue])" color="primary"
                                       :value="props.row[col.name]/props.row[col.projectionMicrochartFullValue]"/>
                    <q-linear-progress v-else-if="col.projectionMicrochartType === 2 && isNumber(props.row[col.name]/props.row[col.projectionMicrochartFullValue])" size="20px" color="primary"
                                       :value="props.row[col.name]/props.row[col.projectionMicrochartFullValue]">
                      <div class="absolute-full flex flex-center">
                        <q-badge color="white" text-color="primary" :label="'' + Math.round(props.row[col.name]/props.row[col.projectionMicrochartFullValue] * 100) + ' %'"/>
                      </div>
                    </q-linear-progress>
                  </template>
                  <template v-else-if="col.projectionChipType">
                    <q-chip v-if="col.projectionChipType === 1" size="13px" square text-color="white" :style="'background-color:'+props.row[col.projectionChipColor]" v-html="props.row[col.name]"/>
                    <q-chip v-if="col.projectionChipType === 2" size="13px" outline :style="'color:'+props.row[col.projectionChipColor]+'; border: 1px solid; border-color:'+props.row[col.projectionChipColor]" v-html="props.row[col.name]"/>
                    <q-chip v-if="col.projectionChipType === 3" size="13px" text-color="white" :style="'background-color:'+props.row[col.projectionChipColor]" v-html="props.row[col.name]"/>
                  </template>
                  <template v-else-if="col.name === 'allowRecordReorder'">
                    <q-btn color="primary" :disabled="props.rowIndex===0 || paginationControl.sortBy !== null" dense outline icon="arrow_drop_up" @click="btnRecordUpDown(0, props.rowIndex)"/>
                    <q-btn color="primary" :disabled="props.rowIndex===queryResult.length-1  || paginationControl.sortBy !== null" dense outline icon="arrow_drop_down" class="q-ml-sm" @click="btnRecordUpDown(1, props.rowIndex)"/>
                  </template>
                  <!-- PHOTO -->
                  <template v-else-if="col.datatypeID===StaticData.DATATYPES.TYPE_PHOTO">
                    <q-img v-if="dwnldFileIsImg(col)" :src="col.value" style="max-width:100px;max-height:100px;cursor:pointer;" @click="selImgCol=col;showDialogWnd=true;"/>
                  </template>
                  <!-- BLOB -->
                  <template v-else-if="col.datatypeID===StaticData.DATATYPES.TYPE_BLOB && col.value">
                    <a href="javascript:void(0)" @click="dwldFile(col, props.cols)" v-if="col.hasBLOBDwldStmt && col.value">{{ col.value }}</a>
                    <q-icon :name="dwnldFileIco(col)" @click="dwldFile(col, props.cols)" size="25px" style="color:#AAAAAA;cursor:pointer;" v-else/>
                  </template>
                  <!-- Inplace edit fields -->
                  <template v-else-if="col.datatypeID===StaticData.DATATYPES.TYPE_BOOLEAN">
                    <q-checkbox size="sm" v-model="props.row[col.name]" true-value="1" false-value="0" :disable="!col.projectionUpdateable" class="table-checkbox-compact"
                                @update:model-value="inlineUpdateColumn($event, col.field, col.name, props.cols, props.row, col.projectionPostUpdateBehaviour)"/>
                  </template>
                  <template v-else-if="col.datatypeID===StaticData.DATATYPES.TYPE_VALUELIST" style="text-align: center">
                    <q-select
                      :model-value="getUpdResValue(props.row[col.name], col.name)"
                      :options="getValueListOptions(props.row, col)"
                      :loading="!!lazyValueListLoading[valueListRowKey(props.row, col.name)]"
                      :popup-content-style="isLazyValueList(col) && valueListStatus(props.row, col) === 'loading' ? {display: 'none'} : undefined"
                      :readonly="!col.projectionUpdateable"
                      :use-input="!col.projectionValuelistStrict"
                      :clearable="!col.projectionValuelistStrict"
                      :fill-input="!col.projectionValuelistStrict"
                      :hide-selected="!col.projectionValuelistStrict"
                      :maxlength="col.lengthMax" class="table-select-compact"
                      @update:model-value="inlineUpdateColumn($event, col.field, col.name, props.cols, props.row, col.projectionPostUpdateBehaviour)"
                      @filter="(val, update) => filterValueListOptionsConditional(val, update, props.row, col.name, col.projectionValuelistStrict)"
                      @popup-show="loadLazyValueList(props.row, col)"
                      @new-value="(val, done) => createNewValue(val, done, col.name)"
                      @input-value="props.row.typingBuffer=$event"
                      @blur="!col.projectionValuelistStrict ? blurInplaceEditValuelist(col, props) : ''"
                      @focusin="selectInlineValuelistInput($event)"
                      filled map-options emit-value dense options-dense input-debounce="0"
                    >
                      <template v-if="isLazyValueList(col)" #no-option>
                        <q-item v-if="valueListStatus(props.row, col) !== 'loading'">
                          <q-item-section role="status" aria-live="polite">
                            {{ $t(valueListStatus(props.row, col) === 'error' ? 'lazyValueListError' : 'lazyValueListEmpty') }}
                          </q-item-section>
                          <q-item-section v-if="valueListStatus(props.row, col) === 'error'" side>
                            <q-btn flat dense color="primary" :label="$t('lazyValueListRetry')"
                                   @click.stop="loadLazyValueList(props.row, col)"/>
                          </q-item-section>
                        </q-item>
                      </template>
                    </q-select>
                  </template>
                  <template v-else-if="col.datatypeID===StaticData.DATATYPES.TYPE_LINK">
                    <a :href="col.value" target="_blank"> {{ col.value }} </a>
                  </template>
                  <template v-else-if="(col.datatypeID===StaticData.DATATYPES.TYPE_INT || col.datatypeID===StaticData.DATATYPES.TYPE_FLOAT || col.datatypeID===StaticData.DATATYPES.TYPE_DOUBLE) && col.projectionUpdateable">
                    <!--                    NUM-{{ props.row["typingBuffer_" + col.name] }}- -->
                    <q-input v-model.number="props.row[col.name]"
                             :error="col.errorMsg != null ? true : undefined"
                             :error-message="col.errorMsg"
                             :maxlength="col.lengthMax"
                             class="table-input-compact"
                             @keydown="(e) => numberKeyhandler(e, col)"
                             @keyup.enter="inlineUpdateNumber(props.row[col.name], col.field, col.name, props.cols, props.row, col)"
                             @blur="blurInplaceEdit(col, props)"
                             @focusin="focusedTableColName=col.name" :ref="`${props.row[rowKey]}_${col.name}`"
                             @update:model-value="props.row['typingBuffer_' + col.name]=$event"
                             type="number" input-class="text-right" dense filled/>
                  </template>
                  <template v-else-if="col.datatypeID===StaticData.DATATYPES.TYPE_STRING && col.projectionUpdateable">
                    <!--                    STR-{{ props.row["typingBuffer_" + col.name] }}- -->
                    <q-input v-model="props.row[col.name]"
                             :ref="`${props.row[rowKey]}_${col.name}`"
                             :maxlength="col.lengthMax"
                             type="textarea"
                             autogrow
                             class="table-textarea-compact"
                             @keydown.enter.prevent="inlineUpdateColumn(props.row[col.name], col.field, col.name, props.cols, props.row, col.projectionPostUpdateBehaviour)"
                             @blur="blurInplaceEdit(col, props)"
                             @focusin="focusedTableColName=col.name"
                             @update:model-value="props.row['typingBuffer_' + col.name]=$event"
                             dense filled style="min-width: 150px"
                    />
                  </template>
                  <template v-else>
                    <q-btn v-if="col.name === 'action'" dense unelevated size="sm" color="primary" @click='btnLoadDetailQuery(props.row)' icon="zoom_in"/>
                    <q-icon v-if="col.name === 'recordIcon'" :name="col.icon"/>
                    <span v-if="col.name !== 'action' && col.name !== 'recordIcon' && !(col.value && col.value.startsWith('ac://'))" v-html="formatValue(col)"/>
                    <q-btn v-if="col.value && col.value.startsWith('ac://')" unelevated :style="col.projectionCSS" :icon="getProjACIcon(col.value)" dense :label="getProjACText(col.value)" @click="callActionWithName(col.value.substring(5))"/>
                  </template>
                </q-td>
              </q-tr>

              <!-- EApplication rendering row-->
              <!-- @dblclick.native="if(!selected.includes(props.row)){toggleSelectedRow(props.row)};btnDoAPPModeAction(1, true)" -->
              <q-tr v-else :props="props" @mouseover.native="hoveredRow=props.row" v-show="showGroup(props.row[groupColName])"
                    @click.exact="toggleSelectedRow(props.row);btnDoAPPModeAction(0, true)"
                    @click.ctrl.exact="toggleSelectedRowCTRL(props.row);btnDoAPPModeAction(0, true)"
                    @click.meta.exact="toggleSelectedRowCTRL(props.row);btnDoAPPModeAction(0, true)"
                    @click.shift.exact="toggleSelectedRowSHIFT(props.row);btnDoAPPModeAction(0, true)"
                    @dblclick="!selected.includes(props.row) && toggleSelectedRow(props.row); btnDoAPPModeAction(1, true)"
                    :class="{
                      'appMode': true,
                      'cssRow' : true,
                      'secLineNoBorder': secondLineColumns.length>0,
                      'selected': selected.includes(props.row)
                    }">
                <q-td>
                  <q-item class="q-px-none" dense>
                    <q-item-section avatar v-if="queryMeta.resultsetMultiselect">
                      <q-checkbox v-model="props.selected" dense @update:model-value="toggleSelectedRowCheckbox($event, props.row);btnDoAPPModeAction(0, true)"/>
                    </q-item-section>
                    <q-item-section avatar v-if="props.row.avatarIcon">
                      <q-icon color="primary" :name="props.row.avatarIcon" class="q-pl-lg-sm" :class="props.row.cssavatarIcon"/>
                    </q-item-section>
                    <q-item-section avatar v-if="props.row.avatarText">
                      <q-item-label v-html="props.row.avatarText" :class="props.row.cssavatarText"/>
                    </q-item-section>

                    <q-item-section top>
                      <template v-if="props.row.leftTop">
                        <q-chip v-if="props.row.chipleftTop" size="13px" :square="props.row.chipleftTop.square" :outline="props.row.chipleftTop.outline" :text-color="props.row.chipleftTop.textColor"
                                :style="getChipEAPPStyle(props.row.chipleftTop.type, props.row[props.row.chipleftTop.colorCol])" v-html="props.row.leftTop"/>
                        <q-item-label v-else overline v-html="props.row.leftTop" :class="props.row.cssleftTop"/>
                      </template>
                      <template v-if="props.row.left">
                        <q-chip v-if="props.row.chipleft" size="13px" :square="props.row.chipleft.square" :outline="props.row.chipleft.outline" :text-color="props.row.chipleft.textColor"
                                :style="getChipEAPPStyle(props.row.chipleft.type, props.row[props.row.chipleft.colorCol])" v-html="props.row.left"/>
                        <q-item-label v-else v-html="props.row.left" :class="props.row.cssleft"/>
                      </template>
                      <template v-if="props.row.leftBottom">
                        <q-chip v-if="props.row.chipleftBottom" size="13px" :square="props.row.chipleftBottom.square" :outline="props.row.chipleftBottom.outline" :text-color="props.row.chipleftBottom.textColor"
                                :style="getChipEAPPStyle(props.row.chipleftBottom.type, props.row[props.row.chipleftBottom.colorCol])" v-html="props.row.leftBottom"/>
                        <q-item-label v-else caption v-html="props.row.leftBottom" :class="props.row.cssleftBottom"/>
                      </template>
                    </q-item-section>

                    <q-item-section side top>
                      <template v-if="props.row.rightTop">
                        <q-chip v-if="props.row.chiprightTop" size="13px" :square="props.row.chiprightTop.square" :outline="props.row.chiprightTop.outline" :text-color="props.row.chiprightTop.textColor"
                                :style="getChipEAPPStyle(props.row.chiprightTop.type, props.row[props.row.chiprightTop.colorCol])" v-html="props.row.rightTop"/>
                        <q-item-label v-else overline v-html="props.row.rightTop" class="q-mt-none" :class="props.row.cssrightTop"/>
                      </template>
                      <template v-if="props.row.right">
                        <q-chip v-if="props.row.chipright" size="13px" :square="props.row.chipright.square" :outline="props.row.chipright.outline" :text-color="props.row.chipright.textColor"
                                :style="getChipEAPPStyle(props.row.chipright.type, props.row[props.row.chipright.colorCol])" v-html="props.row.right"/>
                        <q-item-label v-else v-html="props.row.right" class="q-mt-none" :class="props.row.cssright"/>
                      </template>
                      <template v-if="props.row.rightBottom">
                        <q-chip v-if="props.row.chiprightBottom" size="13px" :square="props.row.chiprightBottom.square" :outline="props.row.chiprightBottom.outline" :text-color="props.row.chiprightBottom.textColor"
                                :style="getChipEAPPStyle(props.row.chiprightBottom.type, props.row[props.row.chiprightBottom.colorCol])" v-html="props.row.rightBottom"/>
                        <q-item-label v-else caption v-html="props.row.rightBottom" class="q-mt-none" :class="props.row.cssrightBottom"/>
                      </template>
                    </q-item-section>
                  </q-item>
                </q-td>
              </q-tr>

              <!-- Second line -->
              <q-tr v-if="secondLineColumns.length>0" :props="props" @click.exact="toggleSelectedRow(props.row);btnDoAPPModeAction(0, true)" @click.ctrl.exact="toggleSelectedRowCTRL(props.row);btnDoAPPModeAction(0, true)"
                    @click.meta.exact="toggleSelectedRowCTRL(props.row);btnDoAPPModeAction(0, true)"
                    @click.shift.exact="toggleSelectedRowSHIFT(props.row);btnDoAPPModeAction(0, true)" @dblclick.native="dblClickRow(props)"
                    v-show="showGroup(props.row[groupColName])" @mouseover.native="hoveredRow=props.row"
                    :class="{
                      'selected': selected.includes(props.row)
                    }">
                <q-td colspan="100%">
                  <template v-for="(secondCol,idx) in secondLineColumns">
                    <div v-html="props.row[secondCol.name]"></div>
                  </template>
                </q-td>
              </q-tr>
            </template>

            <!------------------------- CARD BODY ----------------------->
            <template #item="props">
              <div class="q-pa-xs col-6 col-sm-4 col-md-3 col-lg-2">
                <q-card class="my-card cursor-pointer" v-ripple clickable @click="clickTableCard(props)">
                  <div style="min-height:100px">
                    <q-img :src="Object.values(props.row)[0]" :style="Object.values(props.row)[4]" class="card-img">
                      <div v-if="Object.values(props.row)[1]" class="absolute-bottom text-h7">{{ Object.values(props.row)[1] }}</div>
                    </q-img>
                  </div>
                  <q-card-section v-if="Object.values(props.row)[2]" v-html="Object.values(props.row)[2]"/>
                </q-card>
              </div>
            </template>

            <template #bottom="scope">
              <div class="full-width q-my-sm-none" v-if="!suppressPagination && queryMeta.recordNavigatorVisible">
                <div class="row recnavi">
                  <div class="col-xs-6 col-sm-4 row items-center justify-start no-wrap">
                    <div>{{ $t('rowsPerPage') }} &nbsp;</div>
                    <q-select v-model="paginationControl.rowsPerPage" :options="rowsPerPageOptions" dense flat borderless/>
                  </div>
                  <div class="col-xs-6 col-sm-4 row justify-xs-end justify-sm-center items-center no-wrap">
                    <q-btn style="display:inline" v-if="scope.pagesNumber > 2" icon="first_page" color="primary" dense flat :disable="scope.isFirstPage" unelevated
                           @click="btnPageination(scope.firstPage)" class="q-mr-xs q-mr-sm-sm"/>
                    <q-btn style="display:inline" icon="chevron_left" color="primary" dense unelevated :disable="scope.isFirstPage"
                           @click="btnPageination(scope.prevPage)" class="q-mr-xs q-mr-sm-sm"/>
                    <div class="q-mr-xs q-mr-sm-sm">
                      {{ scope.pagination.page }}/{{ scope.pagesNumber }}
                    </div>
                    <q-btn icon="chevron_right" color="primary" dense unelevated :disable="scope.isLastPage" @click="btnPageination(scope.nextPage)"
                           class="q-mr-xs q-mr-sm-sm"/>
                    <q-btn v-if="scope.pagesNumber > 2" icon="last_page" color="primary" dense flat :disable="scope.isLastPage" @click="btnPageination(scope.lastPage)" unelevated/>
                  </div>
                  <div class="gt-xs col-sm-4 items-center row justify-end">
                    <q-pagination v-model="paginationControl.page" @click="btnPageination()" color="primary" size="sm" :max="scope.pagesNumber" :max-pages="6" :boundary-numbers="true" unelevated/>
                  </div>
                </div>
              </div>
            </template>

            <!------------------------- Custom empty table message ----------------------->
            <template v-slot:no-data>
              <div class="text-center q-pa-md q-mt-lg">
                <q-icon name="warning" size="2em" class="q-mr-sm"/>
                <span style="font-size:15px">{{ noDataLabel }}</span>
              </div>
            </template>
          </q-table>
          <q-btn fab icon="add" @click="btnDoQryAction(floatingAddAction, true)"
                 color="primary" text-color="white"
                 class="btnFabAction" v-if="queryMeta.renderModeID != StaticData.QUERY_RENDERMODE.EAPPLICATION_RENDERER && floatingAddAction"
          />


          <!--------------- CONTEXT MENUE FOR ROW SELECTION -------------->
          <q-menu
            ref="contextMenu"
            v-if="queryExecuteDone"
            touch-position
            context-menu
            @before-show="beforeContextMenu(hoveredRow)"
          >
            <q-list style="min-width: 100px">
              <q-item clickable v-close-popup v-if="lastSelectedRowForCopy && copyClipboardAllowed" @click="showCopyDlg = true">
                <q-item-section side>
                  <q-icon name="content_copy"/>
                </q-item-section>
                <q-item-section>{{ $t('copyToClipboard') }}</q-item-section>
              </q-item>
              <q-separator/>
              <q-item clickable v-close-popup v-if="queryMeta.resultsetShowsinglerecord  && !embedded" @click="selTab = QUERY_TABS.DETAIL">
                <q-item-section side>
                  <q-icon name="pageview"/>
                </q-item-section>
                <q-item-section>{{ $t('showDetails') }}</q-item-section>
              </q-item>
              <q-item clickable v-close-popup v-if="isUpdateable && !embedded" @click="selTab = QUERY_TABS.EDIT">
                <q-item-section side>
                  <q-icon name="edit"/>
                </q-item-section>
                <q-item-section>{{ $t('edit') }}</q-item-section>
              </q-item>
              <q-separator v-if="!embedded"/>
              <q-item clickable v-close-popup v-ripple v-for="action in queryActionList.filter(a => a.visible)" :key="action.id" :disable="getDisabledActions(selected, action)"
                      @click="btnDoQryAction(action, true)">
                <q-item-section side>
                  <q-img v-if="action.iconClass && action.iconURL.indexOf('ico-flash')==-1" :src="getIconURL(action)" style="width:24px"/>
                  <q-icon v-else-if="action.iconClass" :name="action.iconClass"/>
                  <q-icon v-else name="flash_on"/>
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ action.title }}</q-item-label>
                  <q-item-label caption lines="2">{{ action.tooltipMsg }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-separator/>
              <q-item @click="exportQuery({type: 2})" clickable v-close-popup v-if="showExport('ods')">
                <q-item-section side>
                  <q-icon name="import_export"/>
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ $t('queryExportODS') }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-item @click="exportQuery({type: 3})" clickable v-close-popup v-if="showExport('csv')">
                <q-item-section side>
                  <q-icon name="import_export"/>
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ $t('queryExportCSV') }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </template>
        <template v-else>
          <div v-html="queryResult[0]['SINGLE_COL_RESULT']"/>
        </template>
      </q-tab-panel>

      <q-tab-panel :name="QUERY_TABS.DETAIL" class="q-pa-none q-pt-sm">
        <SingleRecordView :queryID="queryID" :columnList="columns" :selectedRow="selected[0]" :paramList="queryParameter" :key="'view_' + (selected[0]?selected[0]['__key__']:'')"/>
      </q-tab-panel>

      <q-tab-panel :name="QUERY_TABS.EDIT" class="q-pa-none q-pt-sm">
        <SingleRecordView :queryID="queryID" :columnList="columns" :selectedRow="selected[0]" :paramList="queryParameter" :editMode="true" @showLoading="(val)=>loading=val"
                          :key="'edit_' + (selected[0]?selected[0]['__key__']:'')"
                          @closeSingleRecordView="closeSingleRecordView()"/>
      </q-tab-panel>

      <q-tab-panel :name="QUERY_TABS.CHART" class="q-pa-none">
        <Parameter :id="'volatileParamBar_' + queryID" v-if="showParameterPanel" :queryMeta="queryMeta" :queryParameter="queryParameter" :appEmbedded="inAppMode"
                   @parameterEntered="parameterEntered" :volatile="true" :key="queryID + queryParameterRefreshCounter"/>

        <Chart v-if="queryExecuteDone" :chartType="chartType" :queryColumns="columns" :queryResult="queryResult" :height="contentHeight" @callActionWithName="callActionWithName" :queryID="queryID"/>
      </q-tab-panel>

      <q-tab-panel :name="QUERY_TABS.PLUGIN" class="q-pa-none no-scroll">
        <Framelink v-if="pluginComponent==='Framelink'" :queryParams="queryParameter" :frameLinkSrc="pluginCompFrameLinkSrc"
                   :height="contentHeight" :key="pluginCompFrameLinkKey" :embedded="true"/>
        <component v-else :is="pluginComponent" :queryID="queryID" :queryColumns="columns" :queryResult="queryResult" :setupToken="pluginSetupToken" :selected="selected"
                   :height="contentHeight" :queryParams="queryParameter" :queryMeta="queryMeta" :embedded="embedded" :key="pluginCompKey" @reloadSubquery="reloadSubqueryFromPlugin"/>
      </q-tab-panel>

      <q-tab-panel :name="QUERY_TABS.WEBENTITY" class="q-pa-none">
        <Webentity :key="webEntityKey" v-if="webentityName != null || webentityID != null" :wedata="webentityData" :id="webentityID" :name="webentityName" :height="contentHeight" :noDataLabel="noDataLabel"/>
      </q-tab-panel>
    </q-tab-panels>

    <!------------------ FILE DOWNLOAD DIALOG ------------------->
    <q-dialog v-model="showDialogWnd" :maximized="imgViewerMaximized"> <!-- :persistent="$q.platform.is.safari" -->
      <q-card style="min-width:70vw;">
        <q-bar>
          <q-space/>
          <q-btn dense flat icon="minimize" @click="imgViewerMaximized = false" :disable="!imgViewerMaximized"/>
          <q-btn dense flat icon="crop_square" @click="imgViewerMaximized = true" :disable="imgViewerMaximized"/>
          <q-btn dense flat icon="close" @click="showDialogWnd = false"/>
        </q-bar>
        <q-card-section :class="{'desktopImgFSSection': !$q.platform.is.mobile}">
          <q-img v-if="selImgCol!=null" :src="selImgCol.value" :class="{'desktopImgFullscreen': !$q.platform.is.mobile}"/>
        </q-card-section>
        <q-card-actions>
          <q-space/>
          <q-btn :label="$t('download')" unelevated @click="dwldFile(selImgCol)"/>
          <q-btn :label="$t('close')" unelevated @click="showDialogWnd=false"/>
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!------------------ COPY DIALOG ------------------->
    <q-dialog v-model="showCopyDlg"> <!-- :persistent="$q.platform.is.safari" -->
      <q-card class="copyDlg" v-if="lastSelectedRowForCopy" style="padding: 10px; width: 900px; max-width: 80vw;">
        <h3 style="font-size: 1.7em">{{ $t('copyToClipboard') }}</h3>
        <q-input :label="$t('querySearch')" filled v-model="clipboardFilter" dense autofocus/>
        <table style="border: 1px solid grey; border-radius: 7px; margin-top: 10px; border-spacing: 0; width: 100%">
          <tr v-for="(x, index) in lastSelectedRowForCopy.filter(element => element[0] && element[0].toLowerCase().includes(clipboardFilter.toLowerCase()))" :key="index" style="max-width: 550px">
            <td style="padding:3px; border-right: 1px solid grey" :style="index + 1 < lastSelectedRowForCopy.length ? 'border-bottom: 1px solid grey': ''">{{ x[0] }}</td>
            <td style="padding:3px;white-space: pre-line;" :style="index + 1 < lastSelectedRowForCopy.length ? 'border-bottom: 1px solid grey': ''"><span v-html="x[1]"></span></td>
          </tr>
        </table>
        <q-card-actions align="right">
          <q-btn :label="$t('close')" unelevated @click="showCopyDlg = false;"/>
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-inner-loading :showing="loading" style="z-index:10">
      <q-spinner size="50px" color="primary" :class="embedded?'':'fixedSpinner'"/>
    </q-inner-loading>
  </div>
</template>

<script>
import download from 'downloadjs'
import {scroll} from 'quasar'
import Parameter from './Parameter'
import SingleRecordView from './SingleRecordView'
import Chart from './charts/Chart'
import Framelink from './Framelink'
import Webentity from './Webentity'
import {QUERY_PLUGIN_COMPONENTS} from './plugins/queryPluginComponents.js'
import {StaticData} from '../classes/StaticData'
import {dialogAvailableHeight} from 'src/classes/DialogLayout.js'
import {markRaw} from 'vue'
import {ActionResultPresenter, formularPrefillFromQuery, mergeFormularPrefill, PLUGIN_VIEW_STATE_KEY, pluginViewState, popupQueryKeyValue, resolveInternalLinkPopup, RESULT_KINDS} from 'src/classes/ActionResultPresenter.js'
import moment from 'moment'
import draggable from 'vuedraggable'
import {useAuthStore} from 'src/stores/AuthStore.js'
import {useQueryStore} from 'src/stores/QueryStore.js'
import CSVHelper from "src/classes/CSVHelper.js";
import {defineAsyncComponent, toRaw} from "vue";
import {cleanHTML} from "components/forms/formsHelper.js";
import URLHelper from "src/classes/URLHelper.js";
import {getQueryExportRequest} from 'src/classes/QueryExportAction.js'

const {getVerticalScrollPosition, setVerticalScrollPosition, getScrollTarget} = scroll

// Constants
// const RETRIEVE_ONLY_KEY_ROW = true

export default {
  name: 'Query',
  components: {
    Parameter, SingleRecordView, Chart, Framelink, Webentity, // DocumentView,
    ...QUERY_PLUGIN_COMPONENTS, draggable
  },
  props: {
    qryID: String,
    embedProp: Boolean,  // lower padding if embedding is set
    showActions: {      // do not render action buttons
      Boolean,
      default: true
    },
    stickyCSS: {        // sticky CSS
      String,
      default: "stickyFullsize"  // top or default
    },
    doAutorefresh: {    // allow autorefreh - if query is called in embedded component, no autrefresh should done
      Boolean,
      default: true
    },
    detailQueryKeyValue: Array,  // query parameter in key, value notation
    dependentQueryIDs: null,
    inAppMode: Boolean,
    suppressPagination: Boolean,
    suppressTitle: Boolean,
    suppressViewModeToggle: Boolean,
    sideQueryCall: Boolean,
    clear: Boolean,
    inDashboard: Boolean,
    dashboardHeaderProp: Boolean,
    appEmbedded: Boolean,
    selQueryKey: String,
    autoExecAction: String,
    popupMode: Boolean,
    // Direct action-result queries use the dialog body as their only vertical scroller.
    dialogScroll: Boolean,
    dialogHeaderTarget: {default: null},
    popupHidewindow: Boolean,
    popupReadonly: Boolean,
    popupAutoclose: Boolean
  },
  data() {
    return {
      StaticData,
      queryID: this.qryID ? this.qryID : this.$route.params.id,
      queryParameter: [],
      autoSkipParameter: history.state.autoSkipParameter,
      queryParameterRefreshCounter: 0,
      queryMeta: {},
      queryKey: "",
      queryResult: [],
      queryActionList: [],
      actionResultPresenter: null, // #897: presenter of the currently executed action (execTarget)
      actionResultCallerUnmounted: false,
      clearSelectedRow: history.state.clearSelectedRow ? history.state.clearSelectedRow : false,
      columns: [],
      inFullscreen: false,
      copyClipboardAllowed: false,
      visibleColumns: [],
      primaryKeyName: "",
      availableColumns: [],
      secondLineColumns: [],
      pageHeight: 300,
      contentHeight: 200,
      tableHeightReady: false,
      divActionHeight: 0,
      separator: 'horizontal',
      filter: {globalSearch: ""},
      filterArrays: {},
      rowKey: "",
      selected: [], // Key of selected row
      selectedTEMP: null, // Key of selected row - given from queryCellUpdate (for no override in restoreSelection
      selectedColumnTEMP: null,
      paginationControl: {rowsPerPage: 0, page: 1},
      rowsPerPageOptions: [25, 50, 100, 200, 300],
      paginationActPage: 0,
      isUpdateable: false,
      showMobileSearch: false,
      queryExecuteDone: false,
      noDataLabel: "",
      orientation: 'portrait',
      cssRow: "cRowL",
      selImgCol: null,
      showDialogWnd: false,
      showCopyDlg: false,
      showPDF: true,
      exportIsRunnimg: false,
      hoveredRow: null,
      btnStopAutorefresh: false,
      autorefreshTime: 100,
      autorefreshIntervallHandler: null,
      showParameterPanel: false,
      singleRecViewEdit: false,
      showSingleRecordViewComp: false,
      chartType: 0,
      showChartPanel: false,
      pluginComponent: null,
      pluginCompKey: 0,
      pluginCompFrameLinkSrc: null,
      pluginCompFrameLinkKey: 0,
      pluginSetupToken: null,
      showPluginPanel: false,
      showWebentity: false,
      loading: true,
      qtabPanelAnimated: true,
      QUERY_TABS: {
        PARAMETER: 'Parameter',
        RESULT: 'Tabelle',
        DETAIL: 'Detail',
        EDIT: 'Edit',
        CHART: 'Chart',
        PLUGIN: 'Plugin',
        WEBENTITY: 'Webentity'
      },
      selTab: this.$route.query && this.$route.query["paramTab"] && this.$route.query["paramTab"] === '1' ? QUERY_TABS.PARAMETER : '',
      webentityID: null,
      webentityName: null,
      webentityData: null,
      dashboardMaximized: false,
      embedded: this.embedProp,
      groupColName: "",
      groupedView: false,
      unshownGroups: [],
      valueListOptions: {},
      lazyValueListOptions: {},
      lazyValueListLoading: {},
      lazyValueListError: {},
      lazyValueListFilter: {},
      lazyValueListRequestId: {},
      lazyValueListCache: {},
      lazyValueListPending: {},
      lazyValueListGeneration: 0,
      lastSelectedKey: null,
      lastSelectedRowForCopy: null,
      lastSelectedWithSHIFT: false,
      imgViewerMaximized: false,
      sideQueryExecDone: false,
      focusedTableColName: null,
      floatingAddAction: null,
      clipboardFilter: "",
      scrollIndex: null,
      scrollingToSelection: false,
      deeplinkActionName: null,
      tableColSortConfig: null,
      webEntityKey: 0,
      navigationActive: false,
      quickLookPending: false,
      vlFilteredOptions: null
    }
  },
  //explicity non reactive arrays used during render, if they were reactive arrays in data block  they would trigger re-renders
  renderedgroups: [],
  renderedActionGroups: [],

  watch: {
    queryParameter: {
      deep: true,
      handler() {
        this.invalidateLazyValueLists()
      }
    },
    filter() {
      if (this.filter.globalSearch && this.filter.globalSearch !== '') {
        localStorage.setItem(this.queryID + '_filterTerm', this.filter.globalSearch)
      } else {
        localStorage.removeItem(this.queryID + "_filterTerm")
      }
    }
  },

  computed: {
    viewMode: {
      get() {
        return useQueryStore().viewModeQuery
      },
      set(value) {
        useQueryStore().viewModeQuery = value
      }
    },

    resolvedTitle() {
      return this.replaceParameterPlaceholder(this.queryMeta.title)
    },
    resolvedInfo() {
      return this.replaceParameterPlaceholder(this.queryMeta.info)
    },

    sortedColumns() {
      if (this.tableColSortConfig) {
        let headerCount = Object.keys(this.tableColSortConfig).length
        return this.columns.sort((h1, h2) => {
          let index1 = this.tableColSortConfig[h1.name] != null ? this.tableColSortConfig[h1.name] : headerCount
          let index2 = this.tableColSortConfig[h2.name] != null ? this.tableColSortConfig[h2.name] : headerCount
          return index1 - index2
        })
      }
      return this.columns
    },

    sortedMultiselectColumns() {
      let result = []
      if (this.queryMeta.resultsetMultiselect && this.queryResult && this.queryResult.length > 0) {
        result.push({multiselect: true})
      }
      result.push(...this.sortedColumns.filter(c => c.width !== '0px'))
      return result
    }
  },

  methods: {
    async btnRecordUpDown(updown, rowIndex) {
      let selectedRow = this.queryResult[rowIndex]
      let nextRow = null
      let prevRow = null
      if (rowIndex + 1 < this.queryResult.length) nextRow = this.queryResult[rowIndex + 1]
      if (rowIndex > 0) prevRow = this.queryResult[rowIndex - 1]

      const queryStore = useQueryStore()

      try {
        await queryStore.queryRecordReorderUpDown({queryID: this.queryID, queryParameter: this.queryParameter, selectedRow: selectedRow, key: selectedRow[this.queryKey], nextRow: nextRow, prevRow: prevRow, updown: updown})

        if (this.appEmbedded) {
          this.$emit("onQueryUpdated")

        } else {
          this.executeQuery()
        }

      } catch (e) {
        logger.error("recordReorderUpDown error", e)
      }
    },

    getUpdResValue(value, name) {
      if (!value) return value;
      const valueStr = String(value);
      if (valueStr.indexOf("|\\|") === -1) return value;
      else return valueStr.split("|\\|")[0]
    },

    getUpdResultValueListOptions(row, colName) {
      let result = this.valueListOptions[colName]

      if (!result) {
        result = []
        let colValue = String(row[colName])
        let valueListCSV = colValue.split("|\\|")[1]
        // logger.info("getUpdResultValueListOptions: valueListCSV: ", valueListCSV)
        if (valueListCSV) {
          let valueParts = valueListCSV.split("=")
          if (valueParts.length === 2 && valueParts[0] === valueParts[1] && valueParts[0].indexOf(";") !== -1) {
            result = valueParts[0]
          }

          let optionItems = valueListCSV.split(";")
          optionItems.forEach(x => {
            if (x.includes("=")) {
              let optArray = x.split("=")
              result.push({label: optArray[0], value: optArray[1]})
            } else {
              result.push({label: x, value: x})
            }
          })
        }
      }
      return result
    },

    valueListRowKey(row, colName) {
      return `${row.__key__}:${colName}`
    },

    invalidateLazyValueLists() {
      this.lazyValueListGeneration++
      this.lazyValueListCache = {}
      this.lazyValueListPending = {}
      this.lazyValueListOptions = {}
      this.lazyValueListLoading = {}
      this.lazyValueListError = {}
      this.lazyValueListFilter = {}
      this.lazyValueListRequestId = {}
    },

    isLazyValueList(col) {
      return col.projectionUpdateable && col.valuelistType === 2 && col.projectionValuelistLazyLoading
    },

    valueListStatus(row, col) {
      const key = this.valueListRowKey(row, col.name)
      if (this.lazyValueListLoading[key]) return 'loading'
      if (this.lazyValueListError[key]) return 'error'
      return this.lazyValueListOptions[key] === undefined ? 'loading' : 'ready'
    },

    getValueListOptions(row, col) {
      const isLazy = this.isLazyValueList(col)
      const key = isLazy ? this.valueListRowKey(row, col.name) : col.name
      if (!isLazy) return this.vlFilteredOptions?.[key] ?? this.getUpdResultValueListOptions(row, col.name)
      if (this.valueListStatus(row, col) !== 'ready') return []
      const options = this.lazyValueListOptions[key] || []
      const filter = this.lazyValueListFilter[key]
      return filter ? options.filter(opt => opt.label.toLowerCase().includes(filter)) : options
    },

    async loadLazyValueList(row, col) {
      if (!this.isLazyValueList(col)) return

      const key = this.valueListRowKey(row, col.name)
      const input = JSON.stringify([this.queryID, this.queryParameter || [], row])
      const cached = this.lazyValueListCache[key]
      if (cached?.input === input && Date.now() < cached.expiresAt) return
      const pending = this.lazyValueListPending[key]
      if (pending?.input === input) return pending.promise
      const requestId = (this.lazyValueListRequestId[key] || 0) + 1
      this.lazyValueListRequestId[key] = requestId
      const generation = this.lazyValueListGeneration
      this.lazyValueListOptions[key] = []
      this.lazyValueListLoading[key] = true
      delete this.lazyValueListError[key]
      if (this.vlFilteredOptions) delete this.vlFilteredOptions[key]

      const promise = this.requestLazyValueList(col, key, input, requestId, generation)
      this.lazyValueListPending[key] = {input, promise}
      return promise
    },

    async requestLazyValueList(col, key, input, requestId, generation) {
      try {
        // Snapshot the same inputs used by the cache key before sessionTouch awaits.
        const [queryID, queryParameter, row] = JSON.parse(input)
        const queryStore = useQueryStore()
        const response = await queryStore.queryCellUpdateValueListDomainGet({
          queryID,
          cellname: col.name,
          row,
          queryParameter
        })
        if (this.lazyValueListGeneration !== generation || this.lazyValueListRequestId[key] !== requestId) return
        this.lazyValueListOptions[key] = (response.valueListValues || '').split(';').filter(Boolean).map(item => {
          const separator = item.indexOf('=')
          return separator === -1
            ? {label: item, value: item}
            : {label: item.substring(0, separator), value: item.substring(separator + 1)}
        })
        this.lazyValueListCache[key] = {input, expiresAt: Date.now() + 60000}
      } catch (error) {
        if (this.lazyValueListGeneration === generation && this.lazyValueListRequestId[key] === requestId) {
          this.lazyValueListError[key] = true
          delete this.lazyValueListCache[key]
          this.eventBus.globalErrorHandling(`${this.$t('valuedomainColumnStart')}${col.label} (${col.name})${this.$t('valuedomainColumnEnd')}`, error)
        }
      } finally {
        if (this.lazyValueListGeneration === generation && this.lazyValueListRequestId[key] === requestId) {
          this.lazyValueListLoading[key] = false
          delete this.lazyValueListPending[key]
        }
      }
    },

    filterValueListOptions(val, update, row, colName) {
      if (this.vlFilteredOptions == null) this.vlFilteredOptions = []
      logger.debug("filterValueListOptions: ", colName, val)
      const col = this.columns.find(column => column.name === colName)
      if (col && this.isLazyValueList(col)) {
        update(() => {
          this.lazyValueListFilter[this.valueListRowKey(row, colName)] = val && val.trim() ? val.toLowerCase() : ''
        })
        return
      }
      const key = col?.projectionValuelistLazyLoading && col.valuelistType === 2 ? this.valueListRowKey(row, colName) : colName
      const allOptions = col?.projectionValuelistLazyLoading && col.valuelistType === 2
        ? this.lazyValueListOptions[key] || []
        : this.getUpdResultValueListOptions(row, colName)
      update(() => {
        if (!val || val.trim() === '') {
          this.vlFilteredOptions[key] = allOptions
        } else {
          const needle = val.toLowerCase()
          this.vlFilteredOptions[key] = allOptions.filter(
            opt => opt.label.toLowerCase().indexOf(needle) > -1
          )
        }
      })
    },

    createNewValue(val, done, model) {
      done(val, 'add-unique')
    },

    filterValueListOptionsConditional(val, update, row, colName, strict) {
      if (!strict) {
        this.filterValueListOptions(val, update, row, colName)
      } else {
        update()
      }
    },

    blurInplaceEditValuelist(col, props) {
      if (props.row["typingBuffer_" + col.name]) {
        this.inlineUpdateValuelistManual(props.row["typingBuffer_" + col.name], col.field, col.name, props.cols, props.row, col.projectionPostUpdateBehaviour)
      }
      props.row["typingBuffer_" + col.name] = null
    },

    blurInplaceEdit(col, props) {
      if (props.row["typingBuffer_" + col.name] != null) {
        this.inlineUpdateColumn(props.row[col.name], col.field, col.name, props.cols, props.row, col.projectionPostUpdateBehaviour)
      }
      props.row["typingBuffer_" + col.name] = null
    },

    selectInlineValuelistInput(evt) {
      setTimeout(() => {
        const target = evt?.target
        if (target) {
          const input = target.tagName === 'INPUT' ? target : target.querySelector('input')
          if (input) {
            this.$nextTick(() => {
              input.select()
            })
          }
        }
      }, 150)
    },

    customSort(rows, sortBy, descending) {
      const data = [...rows]
      const sortColumnMeta = this.columns.find(element => element.name === sortBy);

      if (sortBy) {
        data.sort((a, b) => {
            const x = descending ? b : a
            const y = descending ? a : b

            if (sortColumnMeta.datatypeID >= 1 &&
              sortColumnMeta.datatypeID <= 6) {
              // numeric sort
              // logger.info("num")
              return parseFloat(x[sortBy]) - parseFloat(y[sortBy])

            } else if (sortColumnMeta.datatypeID === 22 || sortColumnMeta.datatypeID === 21) {
              // logger.info("date")
              const xDate = this.parseDate(x[sortBy])
              const yDate = this.parseDate(y[sortBy])

              if (isNaN(xDate) && isNaN(yDate)) {
                return 0; // Beide sind ungültige Daten
              } else if (isNaN(xDate)) {
                return 1; // Ungültige Daten sortieren wir nach hinten
              } else if (isNaN(yDate)) {
                return -1; // Ungültige Daten sortieren wir nach hinten
              }

              return xDate > yDate ? 1 : xDate < yDate ? -1 : 0;

            } else {
              // string sort
              // logger.info("string")
              return x[sortBy] > y[sortBy] ? 1 : x[sortBy] < y[sortBy] ? -1 : 0
            }
          }
        )
      }

      return data
    },

    parseDate(dateStr) {
      if (!dateStr) {
        // Wenn das Datum leer oder null ist, geben wir ein ungültiges Datum zurück.
        return NaN;
      }

      if (dateStr) {
        dateStr = dateStr.trim()
      }

      if (dateStr.includes(".")) {
        // Format: dd.mm.yyyy oder dd.mm.yyyy HH:mm
        const [datePart, timePart] = dateStr.split(" ");
        const [day, month, year] = datePart.split(".");
        return new Date(`${year}-${month}-${day}${timePart ? "T" + timePart : ""}`);

      } else if (dateStr.includes("/")) {
        // Format: dd/mm/yyyy oder yyyy/mm/dd, mit optionalem HH:mm
        const [datePart, timePart] = dateStr.split(" ");
        const parts = datePart.split("/");
        if (parts[0].length === 4) {
          // Format: yyyy/mm/dd
          return new Date(`${parts[0]}-${parts[1]}-${parts[2]}${timePart ? "T" + timePart : ""}`);
        } else {
          // Format: dd/mm/yyyy
          const [day, month, year] = parts;
          return new Date(`${year}-${month}-${day}${timePart ? "T" + timePart : ""}`);

        }
      } else if (dateStr.includes("-")) {
        // Format: yyyy-mm-dd mit optionalem Zeitanteil
        return new Date(dateStr); // Direktes Parsen funktioniert für diesen Fall
      }

      // Rückgabe für unbekannte oder ungültige Formate
      return NaN;
    },

    showContextMenu(evt, row) {
      evt.preventDefault();
      this.$nextTick(() => {
        this.$refs.contextMenu.show(evt);
      });
    },

    isViewModeCard() {
      return useQueryStore().viewModeQuery === StaticData.VIEW_MODES_QUERY.CARD
    },

    numberKeyhandler: function (event, col) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        return
      }

      const currentValue = String(event.target.value)
      if (col.lengthMax && currentValue.length >= col.lengthMax && event.key !== 'Backspace' && event.key !== 'Delete' && event.key !== 'Tab' && event.key !== 'Enter') {
        event.preventDefault()
      }
    },

    localStoreInvisibleColumns() {
      let invisibleAvailableColnames = []
      this.availableColumns.forEach(aCol => {
        if (!this.visibleColumns.includes(aCol.name)) {
          invisibleAvailableColnames.push(aCol.name)
        }
      })
      localStorage.setItem(this.queryID + '__invisibleColumns', invisibleAvailableColnames)
    },

    resetGroups() {
      this.$options.renderedGroups = []
      return true
    },

    renderGroup(groupColName) {
      if (this.$options.renderedGroups.includes(groupColName)) {
        return false
      } else {
        this.$options.renderedGroups.push(groupColName)
        return true
      }
    },

    unshowGroup(groupColName) {
      if (this.unshownGroups.includes(groupColName)) {
        this.unshownGroups = this.unshownGroups.filter(x => x !== groupColName)
      } else {
        this.unshownGroups.push(groupColName)
      }
    },

    showGroup(groupColName) {
      if (this.unshownGroups) {
        if (this.unshownGroups.includes(groupColName)) {
          return false
        }
      }
      return true
    },

    getMask(col) {
      let mask = "N"
      for (let i = 0; i < col.projectionCommaPre; i++) {
        mask += "#"
      }
      mask += ","
      for (let j = 0; j < col.projectionCommaPost; j++) {
        mask += "#"
      }
      return mask
    },

    numberize(value) {
      if (!value || value == "") {
        return 0
      } else {
        return value.replace(",", ".")
      }
    },

    inlineUpdateNumber(value, field, name, columns, row, col) {
      if (col.datatypeID === StaticData.DATATYPES.TYPE_INT) {
        this.inlineUpdateColumn(value, field, name, columns, row, col.projectionPostUpdateBehaviour)

      } else if (this.commaPostValidate(value, col) && this.commaPreValidate(value, col)) {
        this.inlineUpdateColumn(value, field, name, columns, row, col.projectionPostUpdateBehaviour)
      }
    },

    inlineUpdateValuelistManual(value, field, name, columns, row, postUpdateBehaviour) {
      row[name] = value
      this.inlineUpdateColumn(value, field, name, columns, row, postUpdateBehaviour)
    },

    commaPostValidate(value, col) {
      col.errorMsg = null
      let sval = ("" + value).replace(",", ".").replace("+", "").replace("-", "")
      let valOK = false

      if (sval.indexOf(".") > -1) {
        let postComma = sval.split(".")
        if (postComma[1].length <= col.projectionCommaPost) valOK = true
      } else {
        valOK = true
      }
      if (!valOK) {
        col.errorMsg = this.$t('maxPostComma') + col.projectionCommaPost
      }
      return valOK
    },

    commaPreValidate(value, col) {
      col.errorMsg = null
      let sval = ("" + value).replace(",", ".").replace("+", "").replace("-", "")
      let valOK = false

      if (!value || col.projectionCommaPre == 0) {
        return true
      }

      if (sval.indexOf(".") < 0) {
        valOK = sval.length <= col.projectionCommaPre

      } else {
        let preComma = sval.split('.')
        if (preComma[0].length <= col.projectionCommaPre) {
          valOK = true
        }
      }
      if (!valOK) {
        col.errorMsg = this.$t('maxPreComma') + col.projectionCommaPre
      }
      return valOK
    },

    inlineUpdateColumn(value, field, name, columns, row, postUpdateBehaviour) {
      // logger.info("inlineUpdateColumn", row[field], ", postUpdateBehaviour:", postUpdateBehaviour, ", groupedView:", this.groupedView)

      // update model to view the new selected value immediatly
      if (row[field] && typeof row[field] === 'string' && row[field].includes("|\\|")) {
        row[field] = value + "|\\|" + row[field].split("|\\|")[1]
      } else {
        row[field] = value
      }
      // logger.info("inlineUpdateColumn set model: ", field, row[field], value)

      const queryStore = useQueryStore()
      let updateRow = Object.assign({}, row)

      Object.keys(updateRow).forEach(key => {
        if (updateRow[key] && typeof updateRow[key] === 'string' && updateRow[key].includes("|\\|")) updateRow[key] = updateRow[key].split("|\\|")[0]
      });

      delete updateRow.__key__
      let myParameter = Object.assign([], this.queryParameter)
      myParameter = myParameter.map((param) => {
        return {name: param.name, value: param.value}
      })

      let keyCol = columns.find(x => x.primaryKey === true)
      if (keyCol) {
        queryStore.queryCellUpdate({
          key: keyCol.value,
          cellname: field,
          cellvalue: value,
          queryID: this.queryID,
          row: updateRow,
          queryParameter: myParameter
        })
          .then((response) => {
            this.invalidateLazyValueLists()
            if (postUpdateBehaviour === 3) { // Abfrage erneut ausführen
              if (this.appEmbedded) {
                // this.loading = true
                this.$emit("onQueryUpdated")

              } else {
                logger.info("executeQuery complete")
                this.executeQuery()
              }

            } else if (postUpdateBehaviour === 4) { // Nur aktuell selektierte Zeile aktualisieren
              logger.info("only replace keyrow", keyCol.value, row["__key__"])
              this.executeQuery(keyCol.value, row["__key__"])

            } else if (postUpdateBehaviour === 2) { // Abfrage unverändert anzeigen
              logger.info("after cellupdate - stay on resultset, no reload")
            }

            let myField = columns.find(col => (field === col.name));
            let message = myField.label + " " + this.$t('columnUpdateSuccess')
            let color = "positive"

            // reposition cursor caret if sOutputMsg contains key parameter (URL notation)
            if (response.sOutputMsg) {

              // key of resultset record to move the caret
              if (this.setSelectionFromOutputMessage(response.sOutputMsg)) {
                let messageWithoutParams = response.sOutputMsg.substring(0, response.sOutputMsg.indexOf("?"))
                if (messageWithoutParams.trim() !== "") {
                  message = messageWithoutParams
                }
              } else {
                logger.info("no key parameter")
                message = response.sOutputMsg
              }

              if (message.toLowerCase().startsWith("error://")) {
                color = "negative"
                message = message.replace("error://", "").replace("ERROR://", "")
              }
            } else {
              logger.info("No sOutputMsg Parameter found")
            }

            this.$q.notify({color, message})

          })
          .catch(error => {
            logger.error("inlineUpdateColumn error", error)
            this.eventBus.globalErrorHandling(this.$t('columnUpdateError') + field + '"', error)
          })
      }
    },

    getEmbeddedViewModetoggleOptions: function () {
      return [
        this.showParameterPanel && {value: this.QUERY_TABS.PARAMETER, slot: 'parameter'},
        {value: this.QUERY_TABS.RESULT, slot: 'result'},
        this.showChartPanel && {value: this.QUERY_TABS.CHART, slot: 'chart'},
        this.showPluginPanel && {value: this.QUERY_TABS.PLUGIN, slot: 'plugin'}
      ].filter(Boolean)
    },

    btnPageination(page) {
      if (page) {
        page()
      }
      let currentPage = this.paginationControl.page
      this.paginationActPage = currentPage
      if (!this.embedded) {
        if (this.appEmbedded) {
          this.$route.query.pageApp = currentPage
        } else {
          this.$route.query.page = currentPage
        }
        this.$router.replace({...this.$router.currentRoute, query: {...this.$route.query}})
      }
      this.queryResult.forEach((row, index) => {
        if (this.selected.includes(row)) {
          if ((this.filter.globalSearch === "") && (this.paginationControl.page == (Math.floor(index / this.paginationControl.rowsPerPage) + 1))) {
            this.restoreScrollPosition()
          } else {
            this.resetScrollPosition()
          }
        }
      })
    },

    btnCopyCol(value) {
      logger.info(value)
    },

    queryBackBtn() {
      if (this.queryMeta.backToParams && this.showParameterPanel && this.selTab !== this.QUERY_TABS.PARAMETER) {  //this.queryMeta.[backButtonConfig] statt false
        this.selTab = this.QUERY_TABS.PARAMETER
      } else {
        this.$router.back()
      }
    },

    onExecuteAndExportQuery(extension, queryParameter = this.queryParameter, preview = false) {
      const queryStore = useQueryStore()
      if (!this.exportIsRunnimg && !this.inAppMode) {
        this.$q.loading.show({message: 'Daten werden exportiert'})
        this.exportIsRunnimg = true
        return queryStore.queryExecuteAndExport({queryID: this.queryID, queryParameter, extension: extension, preview})
          .then((result) => {
            if (preview) this.previewOrDownloadFile(result)
            this.exportSuccess(preview ? result.filename : result)
          })
          .catch(error => {
            logger.info("ERROR:", error)
            this.exportError(error)
          })
      }
    },

    exportQuery(data) {
      const authStore = useAuthStore()
      const queryStore = useQueryStore()
      if (!this.exportIsRunnimg) {
        this.$q.loading.show({message: 'Daten werden exportiert'})
        this.exportIsRunnimg = true
        let startExport = false
        this.columns.forEach(x => {
          if (x.projectionExport === true) {
            startExport = true
          }
        })
        if (startExport) {
          if (data.type === 1) {
            queryStore.execAndDownload({queryID: this.queryID, queryParameter: this.queryParameter})
              //this.execAndDownloadTest({queryID: this.queryID, queryParameter: this.queryParameter}) // for cypress tests
              .then((filename) => {
                this.exportSuccess(filename)
              })
              .catch(error => {
                this.exportError(error)
              })
          } else if (data.type == 2) {
            queryStore.execAndDownloadOds({queryID: this.queryID, queryParameter: this.queryParameter})
              .then((filename) => {
                this.exportSuccess(filename)
              })
              .catch(error => {
                this.exportError(error)
              })
          } else if (data.type == 3) {
            queryStore.execAndDownloadCsv({
              queryID: this.queryID,
              queryParameter: this.queryParameter,
              separator: authStore.queryExportOptions.separator,
              stringEnclosure: authStore.queryExportOptions.stringEnclosure
            })
              .then((filename) => {
                this.exportSuccess(filename)
              })
              .catch(error => {
                this.exportError(error)
              })
          }
        } else {
          this.eventBus.globalErrorHandling(this.$t('exportNoColumnIncluded'), new Error())
        }
      }
    },

    exportSuccess(filename) {
      this.$q.loading.hide()
      this.exportIsRunnimg = false
      this.$q.notify({color: 'positive', message: this.$t('downloadOf') + filename + this.$t('downloadOfSuccess')})
      //this.$q.notify({color: "positive", message: "Download von '" + filename.data + "' erfolgreich.'"}) // for cypress tests
    },

    exportError(error) {
      this.$q.loading.hide()
      console.error(error)
      this.exportIsRunnimg = false
      this.eventBus.globalErrorHandling(this.$t('exportError'), error)
    },

    dwnldFileIsImg(col) {
      let result = false
      if (col.value) {
        let ext = col.value.split(';')[0].split('/')[1]
        if (ext !== undefined) {
          switch (ext.toLowerCase()) {
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif':
            case 'svg':
              result = true
          }
        }
      }
      return result
    },

    dwldFile(col, row) {
      logger.info("call dwldFile", col.hasBLOBDwldStmt, col)

      if (col.hasBLOBDwldStmt) {
        this.doDownloadFile(col, row)

      } else if (col.value) {
        this.downloadBase64(col, row)
      }
    },

    async doDownloadFile(col, row) {
      logger.info("call doDownloadFile")

      try {
        this.$q.loading.show()
        const queryStore = useQueryStore()
        let dwldResponse = await queryStore.downloadFile(this.buildDwldFileConnectionString(col, row))
        this.previewOrDownloadFile(dwldResponse)

      } catch (error) {
        this.$q.notify({color: "negative", message: this.$t('downloadError')})
        this.eventBus.globalErrorHandling(this.$t('fileDownloadError'), error)

      } finally {
        this.$q.loading.hide()
      }
    },

    previewOrDownloadFile(dwldResponse, quickLook = false) {
      if (/\.pdf$/i.test(dwldResponse.filename) ||
        (quickLook && /\.(docx|xlsx|pptx)$/i.test(dwldResponse.filename))) {
        this.eventBus.printPreview(dwldResponse.data, dwldResponse.filename)

      } else if (quickLook) {
        this.$q.notify({type: 'warning', message: 'Für dieses Dateiformat ist keine Vorschau verfügbar. Unterstützt werden PDF, DOCX, XLSX und PPTX.'})

      } else {
        // download with download.js
        download(dwldResponse.data, dwldResponse.filename)
      }
      // this.$q.notify({color: "positive", message: this.$t('downloadSuccess')})
    },

    buildDwldFileConnectionString(col, row) {
      // logger.info("call buildDwldFileConnectionString", row)
      let paramString = ""
      let label = ""
      if (row) {
        if (row instanceof Array) {
          row.forEach((row, index) => {
            if (row.value) paramString += row.name + "=" + row.value + "&"
            if (col.name === row.name) label = row.value
          })
        } else {
          for (let key in row) {
            if (row.hasOwnProperty(key)) {
              if (row[key]) paramString += key + "=" + row[key] + "&"
              if (col.name === key) label = row[key]
            }
          }
        }
      }
      if (this.queryParameter) {
        this.queryParameter.forEach((param, index) => {
          if (param.value) paramString += param.name + "=" + encodeURIComponent(param.value) + "&"
        })
      }
      paramString = cleanHTML(paramString)
      return "file://sqldec@" + col.id + "?fldLblFrmFld=" + label + "&" + paramString
    },

    downloadBase64(col, row) {
      logger.info("call downloadBase64")
      let ext = col.value.split(';')[0].split('/')[1]
      let mime = col.value.split(';')[0].split(':')[1]
      let filename = null

      if (row) {
        row.forEach((myCol) => {
          // logger.info(myCol.label.toLowerCase().trim(), "==", (col.label.toLowerCase().trim() + ".dlfilename"))
          if (myCol.label) {
            if (myCol.label.toLowerCase() === (col.label.toLowerCase() + ".dlfilename")) {
              filename = myCol.value + "." + ext
            }
          }
        })
      }
      if (!filename) {
        filename = this.queryMeta.title + '-' + col.label + '.' + ext
      }

      //logger.info("filename:", filename)
      download(col.value, filename, mime)
      this.$q.notify({color: "positive", message: this.$t('downloadSuccess')})
    },

    dwnldFileIco(col) {
      let result = "insert_drive_file"
      if (col.value) {
        let ext = col.value.split(';')[0].split('/')[1]
        if (ext !== undefined) {
          switch (ext.toLowerCase()) {
            case 'xls':
            case 'xlsx':
              result = "table_chart"
              break
            case 'pdf':
              result = "fas fa-file-pdf" // picture_as_pdf
              break

            case 'doc':
            case 'docx':
              result = "library_books"
              break

            case 'ppt':
            case 'pptx':
              result = "movie"
              break
          }
        }
      }
      return result
    },

    dblClickRow(props) {
      if (!this.embedded && this.queryMeta.resultsetShowsinglerecord) {
        if (!this.selected.includes(props.row)) {
          this.toggleSelectedRow(props.row)
        }
        this.selTab = this.QUERY_TABS.DETAIL
      } else {
        if (!this.selected.includes(props.row)) {
          this.toggleSelectedRow(props.row)
        }
        this.btnDoAPPModeAction(1, true)
      }
    },

    async clickTableCard(props) {
      logger.info("click card:", props.row)

      let action = props.row.action

      if (action && action.length >= 46) {
        if (action.startsWith("query://")) {
          let queryID = action.substring(8, 46)
          let queryParam = action.includes("?") ? action.split("?")[1].replace("query://", "") : ""
          this.handleQueryTarget(queryID, queryParam)
        }
      }
    },

    handleQueryTarget(queryID, queryParam) {
      let globalQueryParam = []
      let paramList = []

      queryParam.split("&").forEach(paramKeyValue => {
        let oneParam = paramKeyValue.split("=")
        let myParam = {name: oneParam[0], value: oneParam[1]}
        paramList.push(myParam)
      })

      Object.keys(this.$route.query).forEach(name => {
        globalQueryParam[name] = this.$route.query[name]
      })

      // logger.info("queryID:", queryID, ", queryParam:", queryParam, ", paramList:", paramList, ", globalQueryParam:", globalQueryParam)

      // #897: result of an executed action with target new tab / popup
      if (this.presentActionResult({
        kind: RESULT_KINDS.QUERY,
        route: {name: 'Query', params: {id: queryID}, query: Object.assign({}, globalQueryParam)},
        state: {detailQueryKeyValue: JSON.stringify(paramList)},
        popup: {queryID, detailQueryKeyValue: popupQueryKeyValue(paramList, globalQueryParam)}
      })) {
        return
      }

      // eApp or Destination Query in same Dashboard?
      // then reload the query in its Dashboard panel
      if ((this.queryMeta.renderModeID === StaticData.QUERY_RENDERMODE.EAPPLICATION_RENDERER && !this.$q.platform.is.mobile) || this.inDashboard) {
        this.$emit('reloadSubquery', {id: queryID, detailQueryKeyValue: paramList, globalQueryParam})

      } else {
        this.$router.push({
          name: "Query",
          params: {
            id: queryID
          },
          state: {
            detailQueryKeyValue: JSON.stringify(paramList)
          },
          query: globalQueryParam
        })
      }
    },

    getDisabledActions(row, action) {
      let result = action.contextrequired && row && row.length === 0

      if (row && row.length === 1 && row[0] && row[0].disabledActions && row[0].disabledActions.length > 0) {
        let disabledFields = row[0].disabledActions
        if (disabledFields[disabledFields.length - 1] === ';') {
          disabledFields = row[0].disabledActions.slice(0, -1)
        }
        // logger.info("disabledActions. before:", row[0].disabledActions, ", after:", disabledFields)
        let actionNameList = disabledFields.split(";")
        actionNameList.forEach(actionName => {
          if (actionName === action.nativename) {
            result = true
          }
        })
      }

      if (row && row.length > 1 && !action.multiselectSupported) {
        result = true
      }

      return result
    },

    toggleSelectedRow(row, e) {
      const queryStore = useQueryStore()

      // do not do anything if we clicked an input element or a quasar button (has html element 'I' e.g. ac:// action download button) within the row in the table
      if (e && e.target && (
        e.target.nodeName === 'INPUT' || e.target.nodeName === 'I'
      ) && this.selected.includes(row)) {
        return
      }

      this.lastSelectedWithSHIFT = false

      if (!this.selected.includes(row) || this.queryMeta.renderModeID === StaticData.QUERY_RENDERMODE.EAPPLICATION_RENDERER) {
        this.selectRow(row)

        // WIP: emit event when one field is declared as EVENT_TRIGGER
        for (let i = 0; i < this.columns.length; i++) {
          if (this.columns[i].nullValueReplacement &&
            this.columns[i].nullValueReplacement.includes('::EVENT_TRIGGER')) {
            //logger.info("emit event", Object.values(row)[i])
            this.eventBus.queryRowSel(this.queryID, this.columns[i], Object.values(row)[i])
            break
          }
        }

      } else {
        this.selected = []
        this.lastSelectedKey = null
        queryStore.remSelQueryRow({queryID: this.queryID, selQueryRow: row})
        this.$emit("selectSingleQueryRow", {row: null, columns: null})
        this.lastSelectedRowForCopy = null
      }
      this.saveScrollPosition()
    },

    queryRowSelEventReceiver({senderQueryID, col, value}) {
      if (senderQueryID !== this.queryID) {
        //logger.info("queryRowSelEventReceiver.", col, value, this.queryMeta.title)

        for (let i = 0; i < this.columns.length; i++) {
          if (this.columns[i].nullValueReplacement &&
            this.columns[i].nullValueReplacement.includes('::EVENT_RECEIVER')) {
            //logger.info("found receiver col:", this.columns[i].label, ", value:", value)
            //logger.info("queryResult:", this.queryResult)

            for (let x = 0; x < this.queryResult.length; x++) {
              if (Object.values(this.queryResult[x])[i] === value) {
                this.selectRow(this.queryResult[x])

                this.$nextTick(() => {
                  let domParent = document.getElementById('qryContent_' + this.queryID)
                  let domSelTR = domParent.querySelector('.selected')

                  if (domSelTR != null) {
                    let scrollContainer = document.getElementById('qryContent_' + this.queryID)
                      .getElementsByClassName('q-table__middle')[0]
                    if (scrollContainer) {
                      const target = getScrollTarget(scrollContainer)
                      let scrollPos = domSelTR.offsetTop - 50
                      //logger.info("scrollPos:", scrollPos)
                      setVerticalScrollPosition(target, scrollPos)
                    }
                  }
                })
                break
              }
            }
            break
          }
        }
      }
    },

    toggleSelectedRowCTRL(row) {
      const queryStore = useQueryStore()
      if (this.queryMeta.resultsetMultiselect) {
        this.lastSelectedWithSHIFT = false
        if (!this.selected.includes(row)) {
          this.selectRowCTRL(row)
        } else {
          this.selected = this.selected.filter((x) => x.__key__ != row.__key__)
          queryStore.remSelQueryRow({queryID: this.queryID, selQueryRow: row})
          this.lastSelectedKey = null
        }
        this.saveScrollPosition()
      }
    },

    toggleSelectedRowSHIFT(row) {
      const queryStore = useQueryStore()
      if (this.queryMeta.resultsetMultiselect && this.lastSelectedKey) {
        let table = this.$refs.queryTable
        let tableData = table.rows
        let lastSelectedIndex = -1
        let rowIndex = -1
        let from = 0
        let to = 0

        for (let i = 0; i < tableData.length; i++) {
          if (tableData[i].__key__ === this.lastSelectedKey) {
            lastSelectedIndex = i
          }
          if (tableData[i].__key__ === row.__key__) {
            rowIndex = i
          }
        }

        from = rowIndex < lastSelectedIndex ? rowIndex : lastSelectedIndex
        to = rowIndex < lastSelectedIndex ? lastSelectedIndex : rowIndex

        if (!this.lastSelectedWithSHIFT) {
          this.selected = []
          queryStore.clearSelQueryRow({queryID: this.queryID})
        }
        for (let j = from; j <= to; j++) {
          this.selected.push(tableData[j])
        }

        this.selected = this.selected.filter((selection, index, self) =>
          index === self.findIndex((x) => (x.__key__ === selection.__key__))
        )

        this.selectRowSHIFT(row)
        this.lastSelectedKey = row.__key__
        this.lastSelectedWithSHIFT = true
      }
    },

    toggleSelectedRowCheckbox(value, row) {
      this.lastSelectedWithSHIFT = false
      const queryStore = useQueryStore()
      if (value) {
        this.storeCurrentSelectedForCopy(row)
        this.lastSelectedKey = row.__key__
        queryStore.setSelQueryRow({queryID: this.queryID, selQueryRowVal: this.selected})
        this.$emit('selectSingleQueryRow', {row, columns: this.columns})
        this.saveScrollPosition()
      } else {
        this.lastSelectedKey = null
        queryStore.remSelQueryRow({queryID: this.queryID, selQueryRow: row})
        this.lastSelectedRowForCopy = null
      }
      this.saveScrollPosition()
    },

    selectRow(row) {
      const queryStore = useQueryStore()
      if (row) {
        this.selected = []
        this.selected.push(row)
        this.storeCurrentSelectedForCopy(row)
        this.lastSelectedKey = row.__key__
        queryStore.setSelQueryRow({queryID: this.queryID, selQueryRowVal: this.selected})
        this.$emit('selectSingleQueryRow', {row, columns: this.columns})
        this.saveScrollPosition()
      }
    },

    selectRowCTRL(row) {
      const queryStore = useQueryStore()
      this.selected.push(row)
      this.storeCurrentSelectedForCopy(row)
      this.lastSelectedKey = row.__key__
      queryStore.setSelQueryRow({queryID: this.queryID, selQueryRowVal: this.selected})
      this.$emit('selectSingleQueryRow', {row, columns: this.columns})
      this.saveScrollPosition()
    },

    selectRowSHIFT(row) {
      const queryStore = useQueryStore()
      this.storeCurrentSelectedForCopy(row)
      queryStore.setSelQueryRow({queryID: this.queryID, selQueryRowVal: this.selected})
      this.saveScrollPosition()
    },

    storeCurrentSelectedForCopy(row) {
      try {
        if (row && this.columns && this.columns.length > 0) {
          this.lastSelectedRowForCopy = Object.entries(row).filter((x) => this.columns.find((y) => y.name === x[0]))
          this.lastSelectedRowForCopy = this.lastSelectedRowForCopy.filter((x) => !("" + x[1]).startsWith("data:"))
          this.lastSelectedRowForCopy = this.lastSelectedRowForCopy.filter((x) => ("" + x[1]) !== ("__key__"))
          this.lastSelectedRowForCopy.forEach((x) => x[0] = this.columns.find((y) => y.name === x[0]).label)
          this.lastSelectedRowForCopy.forEach((x) => x[1] = x[1] ? x[1].replace("\n", "<br/>") : x[1])
        }
      } catch (e) {
        logger.info("Error in storeCurrentSelectedForCopy: ", e)
      }
    },

    beforeContextMenu(hoveredRow) {
      if (!this.selected.includes(hoveredRow)) {
        this.selectRow(hoveredRow)
      }
    },

    saveScrollPosition() {
      const queryStore = useQueryStore()
      try {
        //let scrollElement = "qryContent_" + this.queryID + " q-table__middle"
        //let scrollElement = "q-table__middle"
        let scrollContainer = document.getElementById('qryContent_' + this.queryID)
          .getElementsByClassName('q-table__middle')[0]

        //if (document.getElementsByClassName("q-table__middle")[0]) {
        // console.warn("scrollPos saved for ", this.queryMeta.title, scrollContainer)

        if (scrollContainer) {
          //const target = getScrollTarget(document.getElementsByClassName("q-table__middle")[0])
          const target = getScrollTarget(scrollContainer)
          let scrollPos = getVerticalScrollPosition(target)
          queryStore.setSelQueryRowScrollPos({queryID: this.queryID, scrollPos})
          //console.warn("saveScrollPosition ", this.queryMeta.title, scrollPos, target)
        }
      } catch (e) {
        //logger.info("Error in saveScrollPosition: ", e)
      }
    },

    restoreScrollPosition() {
      // need 2nd wait of tick here!
      const queryStore = useQueryStore()
      this.$nextTick(() => {
        if (document.getElementById('qryContent_' + this.queryID) != null) {
          let scrollContainer = document.getElementById('qryContent_' + this.queryID)
            .getElementsByClassName('q-table__middle')[0]
          //let scrollElement = "qryContent_" + this.queryID + " q-table__middle"
          //if (document.getElementsByClassName("q-table__middle")[0] && !this.embedded) {
          if (scrollContainer) {
            //const target = getScrollTarget(document.getElementsByClassName("q-table__middle")[0])
            const target = getScrollTarget(scrollContainer)
            let scrollPos = queryStore.getSelQueryRowScrollPos(this.queryID)
            if (scrollPos == -1 && this.scrollIndex != null) {
              if (this.scrollIndex > 5) {
                let scrollElement = scrollContainer.getElementsByTagName('tr')[this.scrollIndex - 1]
                scrollElement.scrollIntoView()
              }
            } else {
              setVerticalScrollPosition(target, scrollPos)
            }
          }
        }
      })
    }
    ,

    resetScrollPosition() {
      // need 2nd wait of tick here!
      this.$nextTick(() => {
        if (document.getElementById('qryContent_' + this.queryID) != null) {
          let scrollContainer = document.getElementById('qryContent_' + this.queryID)
            .getElementsByClassName('q-table__middle')[0]
          if (scrollContainer && !this.embedded) {
            let scrollElement = scrollContainer.getElementsByTagName('tr')[0]
            scrollElement.scrollIntoView()
          }
        }
      })
    }
    ,

    callActionWithName(actionName) {
      actionName = actionName.substring(0, actionName.indexOf(';'))
      if (this.queryActionList.length > 0) {
        let actionFound = false
        for (let i = 0; i < this.queryActionList.length; i++) {
          let action = this.queryActionList[i]
          //logger.info("compare action.id: ", action.id, "==", actionName)

          if (action.nativename == actionName) {
            let tryRowSelCount = 0
            let tryCallAction = setInterval(() => {
              if (this.selected[0]) {
                clearInterval(tryCallAction)
                this.btnDoQryAction(action, true)
                actionFound = true
                logger.info('selRow found! try was: ', tryRowSelCount)

              } else {
                // try 5 times till row is selected...
                logger.info('NO selRow. try : ', tryRowSelCount)
                if (tryRowSelCount > 5) {
                  clearInterval(tryCallAction)
                }
                tryRowSelCount++
              }
            }, 200)
          }
        }
      }
    }
    ,

    autoExecActionFromDeepLink() {
      let ac = false
      let aac = false
      const authStore = useAuthStore()
      if (this.autoExecAction) {
        this.deeplinkActionName = this.autoExecAction
        ac = true
      } else if (authStore.queryActionNativeNameFromDeepLink && !this.appEmbedded) {
        this.deeplinkActionName = authStore.queryActionNativeNameFromDeepLink
        ac = true
      } else if (authStore.queryAppActionNativeNameFromDeepLink && this.appEmbedded) {
        this.deeplinkActionName = authStore.queryAppActionNativeNameFromDeepLink
        aac = true
      }

      // logger.info("!!! autoExecActionFromDeepLink:", this.deeplinkActionName, this.queryActionList)

      if (this.deeplinkActionName && this.queryActionList.length > 0) {
        let actionFound = false
        for (let i = 0; i < this.queryActionList.length; i++) {
          let action = this.queryActionList[i]
          //logger.info("compare action.id: ", action.nativename, "==", this.deeplinkActionName)

          if (action.nativename === this.deeplinkActionName) {
            if (authStore.queryActionCxtParamFromDeepLink) {
              action.pluginParamcontext = authStore.queryActionCxtParamFromDeepLink
            }
            this.btnDoQryAction(action, true)
            actionFound = true
            if (this.deeplinkActionName === authStore.queryActionNativeNameFromDeepLink) {
              authStore.queryActionNativeNameFromDeepLink = '' // only use it once per deep link call
            }
            if (this.deeplinkActionName === authStore.queryAppActionNativeNameFromDeepLink) {
              authStore.queryAppActionNativeNameFromDeepLink = '' // only use it once per deep link call
            }
            break
          }
        }
        if (!actionFound && !this.embedded && (
          !this.appEmbedded && ac || this.appEmbedded && aac
        )) {
          this.$q.dialog({
            title: this.$t('actionCallErrorTitle'),
            message: this.$t('actionCallErrorDescStart') + this.deeplinkActionName + this.$t('actionCallErrorDescEnd')
          })
        }
        this.deeplinkActionName = null // only use it once per deep link call
        //queryActionCxtParamFromDeepLink: null,
      }
    }
    ,

    setAutoRefresh() {
      if (this.queryMeta.autorefresh && this.doAutorefresh) {
        clearInterval(this.autorefreshIntervallHandler)
        this.autorefreshIntervallHandler = setInterval(() => {
          if (!this.btnStopAutorefresh) {
            this.autorefreshTime--
            //this.autorefreshTime-=15
            if (this.autorefreshTime < 0) {
              this.autorefreshTime = 100
              this.removeLocalStorageWithPrefix('as_done_')
              if (PP_CONST.APP_THEME[PP_CONST.APP_ACTIVE].AUTOREFRESH_METHOD === 'reactive' && !this.pluginComponent) {
                this.reload()
              } else {
                window.location.reload()
              }
            }
          }
        }, 2000)
      }
    }
    ,


    setSelectionFromOutputMessage(outputMsg) {
      const keyParameter = URLHelper.getParamValue(outputMsg, 'key')
      if (keyParameter) {
        this.selectedTEMP = keyParameter.includes(';') ? keyParameter.split(';') : keyParameter
        this.selectedColumnTEMP = null
        return true
      }

      const queryString = outputMsg?.split('?').slice(1).join('?')
      if (!queryString) return false

      const selectionParameter = [...new URLSearchParams(queryString).entries()]
        .find(([name]) => name.startsWith('sel.') && name.length > 4)
      if (!selectionParameter) return false

      const [name, csv] = selectionParameter
      this.selectedColumnTEMP = {name: name.slice(4), values: csv.split(';')}
      this.selectedTEMP = null
      return true
    },

    restoreSelection() {
      // logger.info("restoreSelection")
      let selectedRowKey = []
      let keyColName = '__key__'  // default - use synthetic key for unique ident of selected row
      let scrollToSelectedKey = false
      let selectedColumn = null
      let selectionFromOutput = false
      const authStore = useAuthStore()
      const queryStore = useQueryStore()
      // called with Deep Link - use primary key value given with request parameter 'k' (beforeEach in router set it in store)
      let deeplinkQueryParams = null
      if (this.$route.query) {
        deeplinkQueryParams = {}
        Object.keys(this.$route.query)
          .forEach(qp => {
            if (qp.indexOf('qp.') === 0) {
              deeplinkQueryParams[qp.replace('qp.', '')] = this.$route.query[qp]
            }
          })
      }

      if (this.selQueryKey) {
        selectedRowKey[0] = this.selQueryKey
        keyColName = this.queryKey

      } else if (this.selectedColumnTEMP) {
        // sel.<nativename> addresses the native column, even if another field has that name.
        const column = this.columns.find(col => col.nativename === this.selectedColumnTEMP.name)
          || this.columns.find(col => !col.nativename && col.name === this.selectedColumnTEMP.name)
        if (column) {
          selectedColumn = column.name
          selectedRowKey = this.selectedColumnTEMP.values
          keyColName = selectedColumn
          scrollToSelectedKey = true
          selectionFromOutput = true
          this.selectedColumnTEMP = null
        }

      } else if (this.selectedTEMP && (Array.isArray(this.selectedTEMP)
        ? this.selectedTEMP.some(key => this.queryResult.some(row => key === row[this.queryKey]))
        : this.queryResult.some(row => this.selectedTEMP === row[this.queryKey]))) {
        selectedRowKey = Array.isArray(this.selectedTEMP) ? this.selectedTEMP : [this.selectedTEMP]
        keyColName = this.queryKey
        scrollToSelectedKey = true
        selectionFromOutput = true
        this.selectedTEMP = null

      } else if (authStore.queryKeyFromDeepLink || authStore.queryAppKeyFromDeepLink) {
        if (authStore.queryKeyFromDeepLink && !this.appEmbedded) {
          selectedRowKey[0] = authStore.queryKeyFromDeepLink
          keyColName = this.queryKey
        }
        if (authStore.queryAppKeyFromDeepLink && this.appEmbedded) {
          selectedRowKey[0] = authStore.queryAppKeyFromDeepLink
          keyColName = this.queryKey
        }

      } else if (queryStore.getSelQueryRow(this.queryID) && queryStore.getSelQueryRow(this.queryID).length > 0) {
        // Restore selection from last click
        keyColName = this.queryKey
        queryStore.getSelQueryRow(this.queryID).forEach((x) => selectedRowKey.push(x[keyColName]))

      } else if (deeplinkQueryParams != null && Object.keys(deeplinkQueryParams).includes(this.queryKey)) {
        keyColName = this.queryKey
        selectedRowKey[0] = deeplinkQueryParams[this.queryKey]

      } else if (authStore.queryKeySelectFirstRow || this.queryMeta.startSingleRecordView) {
        keyColName = this.queryKey
        if (!keyColName) {
          console.warn("Attention " + this.queryMeta.title + ": First record cannot automatic seleted, because no key column is defined.")
        }

        if (this.queryResult.length > 0) {
          selectedRowKey[0] = this.queryResult[0][keyColName]
        }
        authStore.queryKeySelectFirstRow = false // only use it once per deep link call
      }

      if (selectedRowKey.length === 1 && selectedRowKey[0] === undefined) {
        selectedRowKey = []
      }

      // reselect row, after reload, browser back or deep link call
      // get key of row from store - and page from request parameter
      let foundSelection = false
      if (selectedRowKey.length > 0) {
        this.selected = this.queryResult.filter(row =>
          selectedRowKey.some(value => selectedColumn
            ? value === String(this.getUpdResValue(row[keyColName], keyColName))
            : value === row[keyColName])
        )
        if (selectionFromOutput) {
          queryStore.setSelQueryRow({queryID: this.queryID, selQueryRowVal: this.selected})
        }
        foundSelection = this.selected.length > 0
        if (foundSelection) {
          this.$emit("selectSingleQueryRow", {row: this.selected[0], columns: this.columns})
          if (selectedRowKey[0] === authStore.queryKeyFromDeepLink) {
            authStore.queryKeyFromDeepLink = '' // only use it once per deep link call
          }
          if (selectedRowKey[0] === authStore.queryAppKeyFromDeepLink) {
            authStore.queryAppKeyFromDeepLink = '' // only use it once per deep link call
          }
        }
        const firstSelectionIndex = this.queryResult.findIndex(row => this.selected.includes(row))
        if (firstSelectionIndex >= 0 && this.filter.globalSearch === "" && this.paginationControl.rowsPerPage > 0) {
          this.paginationControl.page = Math.floor(firstSelectionIndex / this.paginationControl.rowsPerPage) + 1
          this.scrollIndex = (firstSelectionIndex % this.paginationControl.rowsPerPage) + 1
          if (scrollToSelectedKey) {
            this.scrollingToSelection = true
            const scrollToFirstSelectedRow = () => {
              const queryContent = document.getElementById('qryContent_' + this.queryID)
              const selectedRow = queryContent?.querySelector('.selected')
              const scrollContainer = queryContent?.getElementsByClassName('q-table__middle')[0]
              if (selectedRow && scrollContainer) {
                setVerticalScrollPosition(getScrollTarget(scrollContainer), selectedRow.offsetTop - 50)
              }
            }
            this.$nextTick(() => {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  scrollToFirstSelectedRow()
                  this.scrollingToSelection = false
                }, 250)
              })
            })
          }
        }
      }
      // no selection found in app mode right side, because a new selection happened on left side, select first element
      if (!foundSelection && this.appEmbedded && !this.popupMode) {
        keyColName = this.queryKey
        if (this.queryResult.length > 0) {
          selectedRowKey[0] = this.queryResult[0][keyColName]
          if (selectedRowKey.length > 0) {
            this.selected = []
            this.queryResult.forEach((row, index) => {
              if (selectedRowKey.includes(row[keyColName])) {
                this.selected.push(row)
                if (this.filter.globalSearch === "") {
                  this.paginationControl.page = Math.floor(index / this.paginationControl.rowsPerPage) + 1
                  this.scrollIndex = (
                    index % this.paginationControl.rowsPerPage
                  ) + 1
                }
                return
              }
            })
          }
        }
      }
    }
    ,

    btnLoadDetailQuery(selectedRow) {
      let detailQueryKey = this.queryMeta.detailqueryKeyname
      let detailQueryKeyvalue = ""
      this.selectRow(selectedRow)
      this.saveScrollPosition()

      for (let key in selectedRow) {
        if (key.startsWith("__")) continue   // internal vue field - ignore it

        if (selectedRow.hasOwnProperty(key)) {
          if (key === this.queryMeta.detailqueryMastername) {
            detailQueryKeyvalue = selectedRow[key]
            break
          }
        }
      }

      this.$router.push({
        name: 'Query',
        params: {
          id: this.queryMeta.detailqueryID
        },
        state: {
          detailQueryKeyValue: JSON.stringify([{name: detailQueryKey, value: detailQueryKeyvalue}])
        },
        query: {page: 1}
      })
        .catch(err => {
        })
    },

    reloadSubqueryFromPlugin(data) {
      this.$emit('reloadSubquery', data)
    },

    async btnPluginCall(objClassID, action, quickLook = false) {
      //logger.info("btnPluginCall - ", action)
      // Temporary: explicit setup opt-in until Portal Server owns resultset stdin.
      // Ordinary plugins and their context parameters retain their existing path.
      try {
        const authStore = useAuthStore()
        const exportRequest = getQueryExportRequest(action, authStore.queryExportfilter?.value,
          this.queryParameter, this.queryMeta.id)
        if (exportRequest) {
          if (this.inAppMode) throw new Error('Diese Exportaktion ist im App-Modus nicht verfügbar.')
          await this.onExecuteAndExportQuery(exportRequest.extension, exportRequest.queryParameter, true)
          return
        }
      } catch (error) {
        this.eventBus.globalErrorHandling('Exportaktion konnte nicht gestartet werden', error)
        return
      }
      if (action.plugin) {
        switch (action.plugin.type) {
          case 4:
            // #897: the plugin view is the result - new tab / popup target
            if (!this.presentPluginResult(action, () => this.callVUEPlugin(objClassID, action))) {
              this.callVUEPlugin(objClassID, action)
            }
            break
          case 6:
            if (!this.presentPluginResult(action, () => this.callIFRAMEPlugin(objClassID, action))) {
              this.callIFRAMEPlugin(objClassID, action)
            }
            break
          default:
            await this.pluginCallBackend(objClassID, action, quickLook)
        }
      } else {
        console.error("Error: no plugin for action configured.")
      }
    },

    /**
     * #897: VUE (type 4) and IFRAME (type 6) plugins render their result in the plugin panel of
     * this query. For the popup target the same plugin view (same component / frame and the same
     * props as the panel) is rendered in the popup; events of the plugin (reloadSubquery) are
     * forwarded to this query like from the panel. For the new tab target the same view inputs are
     * handed over once to the plugin page (route 'Plugin', Plugin.vue) of the new tab. In both
     * cases the view is rendered only there, not in the panel as well.
     * Plugins without view (noGUI) keep their previous behaviour.
     * @return {boolean} true when the presenter took over
     */
    presentPluginResult(action, showInCurrentWindow) {
      if (!this.actionResultPresenter || !this.actionResultPresenter.handlesResults) {
        return false
      }
      const queryParams = (this.queryParameter || []).map(param => ({name: param.name, value: param.value}))
      let popup = null
      let view = null
      if (action.plugin.type === 6) {
        popup = {frameLinkSrc: action.plugin.url, queryParams}
        view = {type: 6, frameLinkSrc: action.plugin.url, queryID: this.queryID, queryParams}
      } else {
        if (action.plugin.paramsStatic === "noGUI") {
          return false
        }
        const name = action.plugin.url
        const component = Object.prototype.hasOwnProperty.call(QUERY_PLUGIN_COMPONENTS, name) ? QUERY_PLUGIN_COMPONENTS[name] : null
        if (component) {
          const props = {
            queryID: this.queryID,
            queryColumns: [...toRaw(this.columns)],
            queryResult: [...toRaw(this.queryResult)],
            setupToken: action.pluginSetupToken,
            selected: [...toRaw(this.selected)],
            queryParams: this.queryParameter,
            queryMeta: this.queryMeta
          }
          popup = {
            component: markRaw(component),
            props: {...props, embedded: this.embedded},
            // plugin events of the popup go to this (calling) query like those of the panel
            onReloadSubquery: data => {
              if (!this.$.isUnmounted) this.reloadSubqueryFromPlugin(data)
            }
          }
          view = {type: 4, component: name, ...props, queryParams: toRaw(this.queryParameter), queryMeta: toRaw(this.queryMeta)}
        }
      }
      const viewState = pluginViewState(view)
      return this.presentActionResult({
        kind: RESULT_KINDS.PLUGIN,
        popup,
        route: viewState ? {name: 'Plugin', params: {id: action.id}} : null,
        state: viewState ? {[PLUGIN_VIEW_STATE_KEY]: viewState} : null,
        showInCurrentWindow
      })
    },

    /**
     * Call VUE plugin (e.g. EAG specific plugins)
     */
    callVUEPlugin(objClassID, action) {
      if (this.inAppMode) {
        this.$emit('execPlugin', {
          componentName: action.plugin.url,
          parameter: action.forwardvalues ? this.selected[0] : ""
        })

      } else if (this.queryMeta.renderModeID === StaticData.QUERY_RENDERMODE.EAPPLICATION_RENDERER && this.$q.platform.is.mobile) {
        this.$router.push({
          name: 'Plugin',
          params: {
            id: action.id,
          },
          state: {
            pluginComponent: action.plugin.url,
            selected: action.forwardvalues ? JSON.parse(JSON.stringify(this.selected)) : []
          }
        })
          .catch(err => {
            console.error(err)
          })

      } else {
        this.pluginSetupToken = action.pluginSetupToken
        this.pluginComponent = action.plugin.url
        this.pluginCompKey++
        this.qtabPanelAnimated = false
        //this.showPluginPanel = true
        this.selTab = this.QUERY_TABS.PLUGIN

        if (action.plugin.paramsStatic === "noGUI") {
          this.$nextTick(async () => {
            this.selTab = this.QUERY_TABS.RESULT
            this.$nextTick(async () => {
              this.qtabPanelAnimated = true
            })
          })
        }
      }
    },

    callIFRAMEPlugin(objClassID, action) {
      //console.warn("callIFRAMEPlugin with action:", action, this.queryParameter)
      this.pluginSetupToken = action.pluginSetupToken
      this.pluginComponent = "Framelink"
      this.pluginCompFrameLinkSrc = action.plugin.url
      this.pluginCompFrameLinkKey++
      this.showPluginPanel = true
      this.selTab = this.QUERY_TABS.PLUGIN
    },

    async pluginCallBackend(objClassID, action, quickLook = false) {
      const queryStore = useQueryStore()
      this.$q.loading.show({message: 'Plugin wird ausgeführt'})

      let amount = 1
      let showSuccess = true
      if (this.queryMeta.resultsetMultiselect && action.pluginMultiselectMode !== 0) {
        amount = this.selected.length
      }

      let propValues = ''
      let key = ''
      for (let i = 0; i < amount; i++) {
        // selected row? build propValues
        if (action.pluginMultiselectMode !== 2) {
          propValues = ''
          key = ''
        }

        // build propvalues parameter - key/value of cols from selected row
        if (this.selected[i] && action.forwardvalues) {
          key += this.selected[i][this.queryKey]
          // build propValues as key=value|key=value
          for (let k in this.selected[i]) {
            if (k.startsWith('__')) {
              continue
            }   // internal vue field - ignore it
            let value = this.selected[i][k]
            propValues += k + "=" + value + "|";
          }
          propValues = propValues.length > 1 ? propValues.slice(0, -1) : propValues
        }

        // query parameter exists? build query parameter as key=value|key=value
        let pluginParams = "queryID='" + this.queryMeta.id + "'"
        if (this.queryParameter) {
          this.queryParameter.forEach((param) => {
            pluginParams += "|" + param.name + "='" + param.value + "'";
          })
        }

        if (action.pluginMultiselectMode !== 2 || i + 1 === amount) {
          this.saveScrollPosition()
          //console.warn("execPluginCall, qryParams: ", pluginParams)

          if (!key || key.replaceAll(";;", "").replaceAll("undefined", "") == "") {
            key = "NONE"
          }

          let multiSelectKeys = ""
          this.selected.forEach(row => {
            multiSelectKeys += row[this.queryKey] + "|"
          })

          let visibleCols = ""
          let orderedVisibleColumns = this.columns.filter(col => this.visibleColumns.includes(col.name))
          orderedVisibleColumns.forEach(ocol => {
            visibleCols += ocol.name + "|"
          })

          try {
            this.$q.loading.show()
            let response = await queryStore.execPluginCall({
              objectInstanceID: key, objClassID, pluginID: action.pluginID, propValues,
              pluginContext: action.pluginParamcontext, pluginParams, multiSelectKeys,
              visibleColumns: visibleCols
            })

            // logger.info("pluginCallBackend respBlob: ", response)
            this.processPluginResponse(response, showSuccess, action, quickLook)

          } catch (error) {
            logger.info("Error in pluginCallBackend: ", error)
            this.eventBus.globalErrorHandling("Error executing query action.", error)

          } finally {
            this.$q.loading.hide()
          }
        }
        if (action.pluginMultiselectMode === 2) {
          key += ";;"
          propValues += ";;"
        }
      }

      this.$q.loading.hide()

      if (action && action.postActionBehaviour === StaticData.ACTION_POST_ACTION_BEHAVIOUR.QUERY_NEW_PARAMETERIZE) {
        this.selected = []
        this.reload()
      } else if (action && (
        action.postActionBehaviour === StaticData.ACTION_POST_ACTION_BEHAVIOUR.UNDEFINED ||
        action.postActionBehaviour === StaticData.ACTION_POST_ACTION_BEHAVIOUR.QUERY_NEW_EXECUTE)) {
        this.selected = []
        this.columns = []
        this.queryResult = []
        this.secondLineColumns = []
        this.executeQuery()
      } else if (action && action.postActionBehaviour === StaticData.ACTION_POST_ACTION_BEHAVIOUR.CALL_OTHER_QUERY && action.postActionQueryID && action.postActionQueryID !== "") {
        this.$router.push({name: 'Query', params: {id: action.postActionQueryID}}).catch(err => {
        })
      }

      this.$emit('execPluginCallDone', {nativename: action.nativename})
      showSuccess = false
    },

    processPluginResponse(response, showSuccess, action, quickLook = false) {
      let vm = this

      if (response) {
        if (response.filename) {
          //logger.info("processPluginResponse previewOrDownloadFile: ", response.filename)
          this.previewOrDownloadFile(response, quickLook)

        } else if (response.iResult !== undefined && response.iResult !== null) {
          switch (response.iResult) {
            case 0:
              if (showSuccess && response.sOutput !== "") {
                let selectedRow = JSON.stringify(this.selected && this.selected.length > 0 ? this.selected[0] : null)
                vm.showOutputMsgFromSQL(response.sOutput, "", action, selectedRow)
              }
              break
            case 1:
              /*
                            if (response.url && response.filename && response.filename.endsWith(".pdf") && !response.filename.includes("?download")) {
                              vm.eventBus.printPreview(response.url, response.filename)
                            } else {
                              vm.$q.notify({color: "positive", message: vm.$t('downloadSuccess')})
                            }
              */
              break
            default:
              vm.$q.dialog({
                title: vm.$t('pluginCallError'),
                message: response.sErrorMsg,
                ok: 'OK',
                style: "width:80%"
              })
          }
        }
      }
    },

    showOutputMsgFromSQL(outputMsg, title, action, selectedRow) {
      if (outputMsg) {
        if (!title) {
          title = ''
        }

        // key of resultset record to move the caret
        this.setSelectionFromOutputMessage(outputMsg)

        if (outputMsg.startsWith('toast://')) {
          let msg = URLHelper.getUrlTextBeforeQuery(outputMsg.replace('toast://', ''))
          if (msg.trim() !== '') {
            this.$q.notify({color: 'positive', message: msg})
          } else {
            logger.info('Plugin outputMsg: ', msg)
          }

        } else if (outputMsg.startsWith("http://") || outputMsg.startsWith("http://")) {
          // #897: new tab uses the tab reserved in the user interaction, popup shows the page in a frame
          if (!this.presentActionResult({kind: RESULT_KINDS.URL, url: outputMsg})) {
            window.open(outputMsg, '_blank');
          }

        } else if (outputMsg.startsWith("https://") && this.presentActionResult({kind: RESULT_KINDS.URL, url: outputMsg})) {
          // #897: https result for target new tab / popup (the same window keeps its previous handling)

        } else if (outputMsg.startsWith('query://')) {
          // Jump to Query with given ID and parameter
          // Format: query://{xxx}?qp.myParm=4711&qp.mySecParm=0815
          let queryID = outputMsg.substr(8, 38)
          let queryParam = outputMsg.includes("?") ? outputMsg.split("?")[1].replace("query://", "") : ""

          this.handleQueryTarget(queryID, queryParam)

          /*
                    this.$router.push({
                      name: 'Query',
                      params: {
                        id: destQueryID
                      },
                      state: {
                        clearSelectedRow: true
                      },
                      query: {page: 1}
                    })
                      .catch(err => {
                      })
          */

        } else if (outputMsg.startsWith("formular://")) {
          // Jump to Formular with given ID and parameter
          // Format: formular://{xxx}?qp.myParm=4711&qp.mySecParm=0815
          let destFormID = outputMsg.substr(11, 38)
          let formQueryParam = {}
          let urlParam = outputMsg.substr(12 + 38)
          let urlParamList = urlParam.split("&");
          for (var i = 0; i < urlParamList.length; i++) {
            let param = urlParamList[i].split('=')
            if (param && param.length > 1) {
              formQueryParam[param[0]] = param[1]
            }
          }

          let oiID = this.selected && this.selected.length > 0 ? this.selected[0].oiID : null
          let forwardValues = action ? action.forwardvalues : false
          let readOnly = action ? action.formReadonly : false

          // #897: result of an executed action with target new tab / popup
          if (this.presentActionResult({
            kind: RESULT_KINDS.FORMULAR,
            route: {name: 'Formular', params: {id: this.queryID, contentid: destFormID}, query: formQueryParam},
            state: {
              selectedRow: selectedRow,
              selectedMultiple: JSON.stringify(forwardValues ? this.selected : null),
              objInstanceID: forwardValues ? oiID : null,
              readonly: readOnly
            },
            popup: {
              contentID: destFormID,
              selectedRow: JSON.stringify({...(safeParseJSON(selectedRow) || {}), ...formularPrefillFromQuery(formQueryParam)}),
              selectedMultiple: forwardValues ? this.selected : null,
              objInstanceID: forwardValues ? oiID : (formularPrefillFromQuery(formQueryParam).oiID || null),
              readonly: readOnly
            }
          })) {
            return
          }

          this.$router.push({
            name: 'Formular',
            params: {
              id: this.queryID,
              contentid: destFormID
            },
            state: {
              selectedRow: selectedRow,
              selectedMultiple: JSON.stringify(forwardValues ? this.selected : null),
              objInstanceID: forwardValues ? oiID : null,
              readonly: readOnly
            },
            query: formQueryParam
          })
            .catch(err => {
            })

        } else if (outputMsg.startsWith("cat://")) {
          // Jump to category (dashboard) with given ID and parameter
          // Format: cat://{xxx}?qp.myParm=4711&qp.mySecParm=0815
          let destCatID = outputMsg.substr(6, 38)

          let queryParam = {}
          let urlParam = outputMsg.substr(7 + 38)
          let urlParamList = urlParam.split("&");
          for (let i = 0; i < urlParamList.length; i++) {
            let param = urlParamList[i].split('=')
            if (param && param.length > 1) {
              queryParam[param[0]] = param[1]
            }
          }

          // #897: result of an executed action with target new tab / popup
          const catSelRow = safeParseJSON(selectedRow)
          if (this.presentActionResult({
            kind: RESULT_KINDS.DASHBOARD,
            route: {name: 'Detail', params: {id: destCatID}, query: queryParam},
            state: {selectedRow: selectedRow, objInstanceID: catSelRow && typeof catSelRow === 'object' ? catSelRow["oiID"] : undefined},
            popup: {categoryID: destCatID, selectedRow: selectedRow}
          })) {
            return
          }

          if (!this.popupMode) {
            let selRow = JSON.parse(selectedRow)
            let oiID = selRow["oiID"]

            this.$router.push({
                name: 'Detail',
                params: {id: destCatID},
                state: {
                  selectedRow: selectedRow,
                  objInstanceID: oiID
                },
                query: queryParam
              }
            )
              .catch(err => {
              })
          }

        } else {
          let dlgTitle = outputMsg.indexOf("error://") > -1 ? "Fehler" : title
          let msg = URLHelper.getUrlTextBeforeQuery(outputMsg.replace("msg://", "").replace("error://", ""))
          if (msg.trim() !== '') {
            this.$q.dialog({
              title: dlgTitle,
              message: msg,
              ok: 'OK',
              style: "width:70%",
              html: true
            })
          }
        }
      }
    },

    async btnDoQryAction(action, checkAskbeforeMsg, quickLook = false) {
      let disabled = this.selected && this.selected.length > 0 ? this.getDisabledActions(this.selected, action) : false
      if (!disabled) {
        if (checkAskbeforeMsg && action.askbeforeMsg) {
          this.$q.dialog({
            title: action.title,
            message: action.askbeforeMsg,
            html: true,
            style: "width:700px;max-width:80vw",
            ok: 'OK',
            cancel: 'Abbrechen',
            persistent: true
          })
            .onOk(() => this.btnDoQryAction(action, false, quickLook))

        } else {
          // #897: present the result according to execTarget. A new tab is reserved here inside the
          // user interaction (popup blocker); the action itself is executed exactly once below.
          const presenter = this.createActionResultPresenter(action)
          if (!presenter.begin()) {
            return
          }
          const previousPresenter = this.actionResultPresenter
          this.actionResultPresenter = presenter

          try {
            const queryStore = useQueryStore()

            if (action.predefNew) {
              queryStore.setSelQueryRow({queryID: this.queryID, selQueryRowVal: null})
            } else {
              this.saveScrollPosition()
            }

            switch (action.type) {
              case 1:
                // Execute Action
                await this.execQryAction(action, quickLook)
                break
              case 2:
                // Formular link
                this.$route.query.pageForBack = this.$route.query.page
                this.$route.query.pageAppForBack = this.$route.query.pageApp
                this.btnActionFormularLink(action, this.selected[0])
                break
              case 3:
                // Link to other query
                this.$route.query.pageForBack = this.$route.query.page
                this.$route.query.pageAppForBack = this.$route.query.pageApp
                this.btnActionQueryLink(action.queryId, action)
                break
              case 4:
                // Plugin Call - perhaps with download afterwards (when plugin type 'command module')
                await this.btnPluginCall("sqlquery", action, quickLook)
                break
              case 5:
                // WebEntity
                this.renderWebentity(action)
                break
              case 6:
                this.execDashboardAction(action)
                break
              case 7:
                if (action.linkURL) {
                  let link = action.linkURL
                  if (this.selected && this.selected.length > 0) {
                    let sel = this.getSelectedRowForAction(action, 0)
                    if (sel) {
                      Object.entries(sel).forEach(([k, value]) => {
                        let regex = new RegExp(":" + k, "g")
                        link = link.replace(regex, "" + value)
                      })
                    }
                  }
                  if (this.queryParameter) {
                    this.queryParameter.forEach((param) => {
                      //logger.info(":" + param.name + "=" + param.value)
                      link = link.replace((":" + param.name), "" + param.value)
                    })
                  }

                  // Wenn Ziel ist innerhalb iPortal - sanftes Router push wenn möglich - Store bleibt erhalten
                  // Problem: CMS Content
                  /*                if (link.startsWith('http') && !link.includes(window.location.host)) {
                                    window.open(link, '_blank')
                                  } else {
                                    this.$router.push(link)
                                  }*/

                  const appLink = this.isInternalAppLink(link)
                  const internalLink = appLink && !link.startsWith("http")

                  // #897: new tab / popup target; links to portal routes are shown with their real portal component in the popup
                  if (this.presentActionResult({
                    kind: appLink ? RESULT_KINDS.INTERNAL_LINK : RESULT_KINDS.URL,
                    url: link,
                    resolved: appLink ? this.resolveInternalLinkPopup(link) : null
                  })) {
                    break
                  }

                  if (internalLink) {
                    // logger.info("Internal app link: ", link)
                    this.$router.push(link)

                  } else {
                    // logger.info("External app link: ", link)
                    // window.open(link, '_blank')
                    location.href = link
                  }
                }
                break
            }
          } finally {
            presenter.end()
            this.actionResultPresenter = previousPresenter
          }
        }
      }
    },

    /**
     * #897: presenter for the result of an action (execTarget same window / new tab / popup).
     */
    createActionResultPresenter(action) {
      return markRaw(new ActionResultPresenter({
        action,
        win: window,
        resolveHref: route => this.$router.resolve(route).href,
        openPopup: data => this.eventBus.openActionResultPopup(data),
        showError: error => this.showActionResultError(error),
        t: key => this.$t(key),
        callerQueryID: this.queryID,
        onCloseButton: () => {
          if (!this.actionResultCallerUnmounted) this.reload()
        }
      }))
    },

    /**
     * #897: returns true when the result of the currently executed action was presented in a new
     * tab or popup (or a concrete error was shown); the caller then skips its same-window path.
     */
    presentActionResult(result) {
      return !!this.actionResultPresenter && this.actionResultPresenter.present(result)
    },

    showActionResultError({title, message, url, alternative}) {
      this.$q.loading.hide()
      if (alternative) {
        // explicit user decision for the previous presentation, no silent target change
        this.$q.dialog({title, message, ok: alternative.label, cancel: this.$t('actionResultCancel')})
          .onOk(() => alternative.run())
        return
      }
      const dialog = this.$q.dialog({
        title,
        message: url ? message + ' ' + url : message,
        ok: url ? this.$t('actionResultOpenInNewTab') : 'OK',
        cancel: url ? this.$t('actionResultCancel') : false
      })
      if (url) {
        // explicit user decision, no silent target change
        dialog.onOk(() => window.open(url, '_blank', 'noopener'))
      }
    },

    /**
     * #897: popup content of an internal portal link (real portal component with the link context)
     * or null when the route has no popup capable component.
     */
    resolveInternalLinkPopup(link) {
      return resolveInternalLinkPopup(link, {
        origin: window.location.origin,
        resolveRoute: (path, query) => {
          const resolved = this.$router.resolve({path, query})
          return resolved && resolved.matched && resolved.matched.length ? {name: resolved.name, params: resolved.params} : null
        }
      })
    },

    isInternalAppLink(link) {
      try {
        const url = new URL(link, window.location.origin)

        if (url.origin !== window.location.origin) {
          return false
        }

        const resolved = this.$router.resolve({
          path: url.pathname,
          query: Object.fromEntries(url.searchParams.entries()),
          hash: url.hash
        })

        if (!resolved?.matched?.length) {
          return false
        }

        const matchedPaths = resolved.matched.map(route => route.path)

        const allowedInternalPaths = [
          '/info',
          '/help',
          '/query/:id',
          '/qapp/:id',
          '/formular/:id/:contentid',
          '/we/:id',
          '/document/:id/:contentid',
          '/parameter/:id',
          '/framelink/:id',
          '/singleRecordView/:id',
          '/releaseNotes',
          '/gmap',
          '/chart',
          '/gantt',
          '/plugin/:id'
        ]

        return matchedPaths.some(path => allowedInternalPaths.includes(path))
      } catch (e) {
        return false
      }
    },

    renderWebentity(action) {
      // #897: result of the action with target new tab / popup
      if (this.presentActionResult({
        kind: RESULT_KINDS.WEBENTITY,
        route: {name: 'Webentity', params: {id: action.webEntityId}},
        state: {wedata: this.selected[0] ? JSON.stringify(this.selected[0]) : undefined},
        popup: {webentityID: action.webEntityId, wedata: this.selected[0] || null}
      })) {
        return
      }

      if (this.queryMeta.renderModeID === StaticData.QUERY_RENDERMODE.EAPPLICATION_RENDERER) {
        if (this.$q.platform.is.mobile) {
          setTimeout(() => {
            this.$router.push({
              name: 'Webentity',
              params: {
                id: action.webEntityId
              },
              state: {
                wedata: JSON.stringify(this.selected[0])
              }
            })
              .catch(err => {
              })
          }, 50)

        } else {
          this.$emit('reloadWebentity', {id: action.webEntityId, wedata: this.selected[0]})
        }
      } else {
        this.webentityID = action.webEntityId
        this.webentityData = this.selected[0]
        this.showWebentity = true
        this.selTab = this.QUERY_TABS.WEBENTITY
        this.webEntityKey++
      }
    },

    btnActionFormularLink(action, selectedRow) {
      // logger.info("!!!!!!!!!!!! btnActionFormularLink: ", action.queryId, this.queryID)
      // let destQueryID = action.queryId // ? action.queryId : this.queryID
      // objectInstance for preFill Formular out of formularListGet must come from a result column with name 'oiID'
      let oiID = null
      if (this.selected.length === 1 && this.selected[0] && this.selected[0].oiID) {
        oiID = this.selected[0].oiID
      }

      let selectedArray = null
      if (this.queryMeta.resultsetMultiselect && action.forwardvalues) {
        selectedArray = this.selected
      }

      let mappedSelectedRow = {}
      if (action.forwardvalues && selectedRow) {
        this.columns.map((col, idx) => {
          // logger.info("col:", col)
          if (col.datatypeID === 500 && col.hasBLOBDwldStmt === 1 && selectedRow[col.name] != null) {
            mappedSelectedRow[col.name] = this.buildDwldFileConnectionString(col, selectedRow)
          } else {
            mappedSelectedRow[col.name] = selectedRow[col.name]
          }
        })
      }

      let myParameter = {}
      this.queryParameter.map((param) => {
        myParameter[param.name] = param.value
      })
      myParameter["sActionReferer"] = action.nativename

      // #897: result of the action with target new tab / popup (same state as the route below)
      if (this.presentActionResult({
        kind: RESULT_KINDS.FORMULAR,
        route: {name: 'Formular', params: {id: this.queryID, contentid: action.objectClassId}, query: this.$route.query},
        state: {
          selectedRow: JSON.stringify(action.forwardvalues ? mappedSelectedRow : {}),
          selectedMultiple: JSON.stringify(selectedArray),
          objInstanceID: action.forwardvalues ? oiID : null,
          queryParameter: JSON.stringify(myParameter),
          readonly: action.formReadonly,
          postAction: action.postActionBehaviour,
          actionSetupToken: action.pluginSetupToken
        },
        popup: {
          contentID: action.objectClassId,
          // same prefill priority as Formular.vue for the route above: a (truthy) selected row value
          // before the query parameter of the same name (case-insensitive), incl. sActionReferer
          selectedRow: JSON.stringify(mergeFormularPrefill(action.forwardvalues ? mappedSelectedRow : {}, myParameter)),
          selectedMultiple: selectedArray,
          objInstanceID: action.forwardvalues ? oiID : null,
          readonly: action.formReadonly
        }
      })) {
        return
      }

      if ((this.queryMeta.renderModeID !== StaticData.QUERY_RENDERMODE.EAPPLICATION_RENDERER || this.$q.platform.is.mobile) && !this.popupMode && !this.appEmbedded) { // && !this.inDashboard
        // logger.info("!!!!!!!!!!!!! Query: Route to formular. myParameter:", myParameter, ", readOnly:", action.formReadonly, selectedArray, ", actionSetupToken:", action.pluginSetupToken)

        if (action.autorun) {
          this.$router.replace({
            name: 'Formular',
            params: {
              id: this.queryID,
              contentid: action.objectClassId
            },
            state: {
              selectedRow: JSON.stringify(action.forwardvalues ? mappedSelectedRow : {}),
              selectedMultiple: JSON.stringify(selectedArray),
              objInstanceID: action.forwardvalues ? oiID : null,
              queryParameter: JSON.stringify(myParameter),
              readonly: action.formReadonly,
              postAction: action.postActionBehaviour,
              actionSetupToken: action.pluginSetupToken
            },
            query: this.$route.query
          })

        } else {
          this.$router.push({
            name: 'Formular',
            params: {
              id: this.queryID,
              contentid: action.objectClassId
            },
            state: {
              selectedRow: JSON.stringify(action.forwardvalues ? mappedSelectedRow : {}),
              selectedMultiple: JSON.stringify(selectedArray),
              objInstanceID: action.forwardvalues ? oiID : null,
              queryParameter: JSON.stringify(myParameter),
              readonly: action.formReadonly,
              postAction: action.postActionBehaviour,
              actionSetupToken: action.pluginSetupToken
            },
            query: this.$route.query
          })
            .catch(err => {
            })
        }
      } else {
        // logger.info("!!!!!!!!!!!!!!! Query: emit reloadFormular", this.queryParameter, "vs", myParameter)
        this.$emit('reloadFormular', {
          id: action.queryId,
          contentid: action.objectClassId,
          callerQueryID: this.queryID,
          selectedRow: action.forwardvalues ? mappedSelectedRow : "",
          queryParameter: myParameter, // this.queryParameter,
          actionTitle: action.title,
          actionNew: action.predefNew,
          selectedMultiple: selectedArray,
          objInstanceID: action.forwardvalues ? oiID : null,
          popupAutoclose: this.popupAutoclose
        })
      }
    },

    btnActionQueryLink(queryID, action) {
      let paramList = []
      let globalQueryParam = {}
      let selectedRow = null

      if (action.forwardvalues) {
        let paramObj = {}
        if (this.queryMeta.resultsetMultiselect) {
          this.selected.forEach(row => {
            for (let key in row) {
              if (key.startsWith("__")) continue   // internal vue field - ignore it
              paramObj[key] = (paramObj[key] ? (paramObj[key] + ",") : "") + (row[key] ? row[key] : "")
            }
          })
          selectedRow = paramObj

        } else {
          selectedRow = this.selected[0]
        }

        for (let key in selectedRow) {
          if (key.startsWith("__")) continue   // internal vue field - ignore it
          let param = {name: key, value: selectedRow[key]}
          paramList.push(param)
        }
      }
      // set global parameter from request parameter
      Object.keys(this.$route.query).forEach(name => {
        globalQueryParam[name] = this.$route.query[name]
      })

      // set parameters from selected row
      for (let key in selectedRow) {
        if (key.startsWith('__')) {
          continue
        }   // internal vue field - ignore it
        for (let x = 0; x < this.columns.length; x++) {
          let col = this.columns[x]
          if (col.name === key) {
            if (col.projectionExport || col.projectionRequestParam) {
              //logger.info("add qp ", key, "=", selectedRow[key])
              globalQueryParam["qp." + key] = selectedRow[key]
            }
            break
          }
        }
      }

      this.saveScrollPosition()

      if (!this.appEmbedded && !this.inDashboard) {
        globalQueryParam["page"] = this.paginationControl.page
      }

      // #897: result of the action with target new tab / popup
      if (this.presentActionResult({
        kind: RESULT_KINDS.QUERY,
        route: {name: 'Query', params: {id: queryID}, query: globalQueryParam},
        state: {detailQueryKeyValue: JSON.stringify(paramList)},
        popup: {queryID, detailQueryKeyValue: popupQueryKeyValue(paramList, globalQueryParam)}
      })) {
        return
      }

      // eApp or Destination Query in same Dashboard?
      // then reload the query in its Dashboard panel
      if ((this.queryMeta.renderModeID === StaticData.QUERY_RENDERMODE.EAPPLICATION_RENDERER && !this.$q.platform.is.mobile) || this.inDashboard) {
        this.$emit('reloadSubquery', {id: queryID, detailQueryKeyValue: paramList, globalQueryParam})

      } else {
        this.$router.push({
          name: "Query",
          params: {
            id: queryID
          },
          state: {
            detailQueryKeyValue: JSON.stringify(paramList)
          },
          query: globalQueryParam
        })
      }
    },

    btnDoAPPModeAction(dblClick, manual) {
      let actionOnClick

      if (dblClick === 0) {
        actionOnClick = this.queryActionList.find((action) => action.id === this.queryMeta.resultSetOnClick)
      } else {
        actionOnClick = this.queryActionList.find((action) => action.id === this.queryMeta.resultSetOnDoubleClick)
      }

      if (actionOnClick) {
        if (manual && !this.popupMode) {
          this.$route.query.pageApp = 1
        }

        this.btnDoQryAction(actionOnClick, true)
      }
    },

    async execQryAction(action, quickLook = false) {
      const queryStore = useQueryStore()
      let showError = true
      let showSuccess = true

      let myParameter = Object.assign([], this.queryParameter)
      myParameter = myParameter.map((param) => {
        return {name: param.name, value: param.value}
      })

      const selectedRows = this.selected.length > 0 ? this.selected : [null]

      this.$q.loading.show({message: this.$t('actionExecuting')})

      try {
        for (let i = 0; i < selectedRows.length; i++) {
          const selectedRow = selectedRows[i]

          let key = null
          if (selectedRow && action.forwardvalues) {
            key = selectedRow[this.queryKey]
          }

          const newSel = selectedRow ? this.getSelectedRowForAction(action, i) : null

          const response = await queryStore.execQueryAction({
            queryID: this.queryID,
            key,
            racID: action.id,
            selectedRow: newSel,
            queryParameter: myParameter
          })

          const isLastRun = i + 1 === selectedRows.length

          if (isLastRun) {
            this.handleQueryActionPostBehaviour(action)
          }

          this.eventBus.refreshComp(StaticData.ROUTE_NAMES.QUERY, this.dependentQueryIDs)
          this.processPluginResponse(response, showSuccess, action, quickLook)
        }
      } catch (error) {
        if (showError) {
          this.eventBus.globalErrorHandling(this.$t('actionCallErrorGlobal'), error)
          showError = false
        }
      } finally {
        this.$q.loading.hide()
      }
    },

    handleQueryActionPostBehaviour(action) {
      const behaviour = action?.postActionBehaviour

      if (behaviour === StaticData.ACTION_POST_ACTION_BEHAVIOUR.QUERY_NEW_PARAMETERIZE) {
        this.selected = []
        if (!this.popupMode) {
          this.reload()
        }

      } else if (behaviour === StaticData.ACTION_POST_ACTION_BEHAVIOUR.UNDEFINED ||
        behaviour === StaticData.ACTION_POST_ACTION_BEHAVIOUR.QUERY_NEW_EXECUTE) {
        this.selected = []
        this.columns = []
        this.queryResult = []
        this.secondLineColumns = []
        this.executeQuery()

      } else if (behaviour === StaticData.ACTION_POST_ACTION_BEHAVIOUR.CALL_OTHER_QUERY &&
        action?.postActionQueryID) {
        this.$router.push({
          name: 'Query',
          params: {id: action.postActionQueryID}
        }).catch(err => {
        })

      } else {
        this.$nextTick(() => {
          this.restoreSelection()
        })
      }
    },

    execDashboardAction(action) {
      this.$route.query.pageForBack = this.$route.query.page
      this.$route.query.pageAppForBack = this.$route.query.pageApp
      // set global parameter from request parameter
      let globalQueryParam = {}
      Object.keys(this.$route.query).forEach(name => {
        globalQueryParam[name] = this.$route.query[name]
      })

      //delete previous params
      Object.keys(globalQueryParam)
        .filter(param => {
          return param.indexOf("qp.") === 0
        }).forEach(param => {
        delete globalQueryParam[param]
      })

      // set parameters from selected row
      if (this.selected[0]) {
        for (let key in this.selected[0]) {
          if (key.startsWith("__")) continue   // internal vue field - ignore it
          for (let x = 0; x < this.columns.length; x++) {
            let col = this.columns[x]
            if (col.name == key) {
              if (col.projectionExport || col.projectionRequestParam) {
                // logger.info("add qp ", key, "=", this.selected[0][key])
                globalQueryParam["qp." + key] = this.selected[0][key]
              }
              break
            }
          }
        }
      }

      if (action.dashboardID) {
        // #897: result of the action with target new tab / popup
        const dashboardSelectedRow = JSON.stringify(action.forwardvalues ? this.selected[0] : {})
        if (this.presentActionResult({
          kind: RESULT_KINDS.DASHBOARD,
          route: {name: 'Detail', params: {id: action.dashboardID}, query: {page: 1, ...this.$route.query}},
          state: {selectedRow: dashboardSelectedRow},
          popup: {categoryID: action.dashboardID, selectedRow: dashboardSelectedRow}
        })) {
          return
        }

        if (this.popupMode) {
          this.$emit('queryResponse', {
            selectedRow: action.forwardvalues ? this.selected[0] : "",
            outputMsg: 'cat://' + action.dashboardID
          })

        } else if (this.queryMeta.renderModeID !== StaticData.QUERY_RENDERMODE.EAPPLICATION_RENDERER || this.$q.platform.is.mobile) {
          this.$router.push({
            name: 'Detail',
            params: {
              id: action.dashboardID
            },
            state: {
              selectedRow: JSON.stringify(action.forwardvalues ? this.selected[0] : {})
            },
            // query: globalQueryParam
            query: {page: 1, ...this.$route.query}
          })
            .catch(err => {
            })

        } else {
          // logger.info("reloadDashboard:", globalQueryParam)
          this.$emit('reloadDashboard', {
            id: action.dashboardID,
            globalQueryParam,
            selectedRow: action.forwardvalues ? this.selected[0] : ""
          })
        }
      }
    }
    ,

    getSelectedRowForAction(action, index) {
      let sel = action.forwardvalues ? this.selected[index] : null
      let newSel = null
      if (sel) {
        newSel = {}
        let wrongChar = false
        Object.entries(sel).forEach(([k, value]) => {
          if (k.includes(" ") || k.includes("/") || k.includes("\\")) {
            wrongChar = true
          } else {
            newSel[k] = value
          }
        })
        if (wrongChar) {
          this.eventBus.globalErrorHandling(this.$t('actionParseError'), new Error())
        }
      }
      return newSel
    }
    ,

    getActionBtnColor(action) {
      if (action.cssClass || action.cssStyle) {
        return ''
      }

      switch (action.menuColor) {
        case 0:
          return 'primary'
        case 1:
          return 'red-14'
        case 2:
          return 'green-10'
        case 3:
          return 'blue-10'
        case 4:
          return 'yellow-14'
        case 5:
          return 'orange-14'
        case 6:
          return 'grey-9'
      }
    }
    ,

    getVisibleActions() {
      let aktionen = this.queryActionList
      // if(this.queryActionList && this.queryActionList.length>0) logger.info("check:", aktionen ? aktionen.filter(a => a.visible) : [])
      return aktionen ? aktionen.filter(a => a.visible) : []
    }
    ,

    getActionGroupIcon(action) {
      let result = 'flash_on'
      if (action) {
        result = action.iconClass
      }
      return result
    }
    ,

    getActionGroupColor(action) {
      let result = 'primary'
      if (action) {
        result = this.getActionBtnColor(action)
      }
      return result
    }
    ,

    resetActionGroups() {
      this.$options.renderedActionGroups = []
      return true
    }
    ,

    renderActionGroup(groupname) {
      if (this.$options.renderedActionGroups.includes(groupname)) {
        return false
      } else {
        this.$options.renderedActionGroups.push(groupname)
        return true
      }
    }
    ,

    getActionsInSameGroup(action) {
      let result = []
      if (action) {
        this.queryActionList.forEach(ac => {
          if (ac.actiongroup == action.actiongroup) {
            result.push(ac)
          }
        })
      }
      return result
    }
    ,

    myFilter(rows, terms, cols, cellValue) {
      // filter values to lower case
      const lowerFilter = Object.fromEntries(
        Object.entries(terms).map(([k, v]) => [k, v.toLowerCase()])
      );

      // get sorted, visible columns
      let visibleColumns = this.getVisibleColumnsForTable()
      let sortedVisibleColumns = this.sortedColumns.filter(col => visibleColumns.includes(col.name))

      // create filter for only sorted visible columns
      const lowerSortedVisibleFilter = {}
      sortedVisibleColumns.forEach(col => lowerSortedVisibleFilter[col.name] = lowerFilter[col.name])

      // globalSearch filter
      let resultRows = rows.filter(
        row => sortedVisibleColumns.some(col => (cellValue(col, row) + '').toLowerCase().includes(lowerFilter.globalSearch))
      )

      // filter by sorted visible column filters
      Object.keys(lowerSortedVisibleFilter).forEach(key => {
        if (lowerSortedVisibleFilter[key] && lowerSortedVisibleFilter[key] != "") {
          let col = sortedVisibleColumns.find(col => col.name == key)
          resultRows = resultRows.filter(
            row => (cellValue(col, row) + '').toLowerCase().includes(lowerSortedVisibleFilter[key])
          )
        }
      })
      return resultRows
    }
    ,

    getIconURL(action) {
      if (action.iconURL) {
        return PP_CONST.HOST + action.iconURL
      }
      return ""
    }
    ,

    tableDrawnCallback() {
      this.$nextTick(() => {
        // Get all SVG tags in the document
        const svgElements = document.querySelectorAll('#queryTable svg');

        // Iterate over the SVG tags
        svgElements.forEach(svgElement => {
          //logger.info("fond element:", svgElement);

          if (svgElement.attributes.hasOwnProperty("v")) {
            let value = svgElement.attributes.v.value
            //logger.info("value:", value)

            JsBarcode(svgElement, value, {
              // format: "EAN8",
              lineColor: '#333333',
              width: 6,
              height: 80,
              displayValue: false
            })
          }
        })
      })
    }
    ,

    formatValue(col) {
      let idx = 0
      let value = col.value
      // logger.info("FormatValue col", col)

      if (value) {
        if (value.startsWith("bc://")) {
          return '<svg class="barcode" v="' + value.replace("bc://", "") + '"></svg>'
        }

        while ((idx = value.indexOf("gsm://")) > -1) {
          let telNr = ""
          let telIdx = idx + 6
          while ((!isNaN(parseInt(value.charAt(telIdx))) || value.charAt(telIdx) === ' ' || value.charAt(telIdx) === '/') && telIdx < value.length) {
            telNr += value.charAt(telIdx)
            telIdx++
          }
          value = value.replace("gsm://" + telNr, "<a href='tel:" + telNr + "'>" + telNr + "</a>")
        }

        if (!isNaN(value)) {
          if (col.datatypeID == StaticData.DATATYPES.TYPE_FLOAT || col.datatypeID == StaticData.DATATYPES.TYPE_DOUBLE) {  // float, double - format to locale
            value = parseFloat(value).toLocaleString('de-DE', {})
          }
        }
      }
      return value
    }
    ,

    resetDataFields() {
      this.queryMeta = {}
      this.queryResult = []
      this.columns = []
      this.queryExecuteDone = false
      this.visibleColumns = []
      this.rowKey = ""      // key for q-table component
      this.queryKey = ""    // configured key from query
      this.noDataLabel = "Keine Daten vorhanden"
      this.availableColumns = []
      this.secondLineColumns = []
      this.exportIsRunnimg = false
      this.singleRecViewEdit = false
      this.showSingleRecordViewComp = false
      this.chartType = 0
      this.showChartPanel = false
      this.contentHeight = 200
      this.tableHeightReady = false
      this.selTab = ''
      this.selected = []
    }
    ,

    reload() {
      const queryStore = useQueryStore()
      //logger.info("!!!!!!!!!!! Reload() ", this.queryMeta.title, this.queryID, ", paramRefreshCounter: ", this.queryParameterRefreshCounter)

      this.resetDataFields()
      this.queryParameter.autoSkipParamsDone = false

      if (this.clearSelectedRow || this.popupMode) {
        history.state.clearSelectedRow = false
        this.clearSelectedRow = false   // only clear once - a later reload has to restore the selection
        queryStore.setSelQueryRow({queryID: this.queryID, selQueryRowVal: []})
      }
      // show loading spinner
      this.loading = true

      queryStore.getQueryMeta(this.queryID)
        .then((queryMeta) => {
          this.queryMeta = queryMeta

          if (!this.embedded && !this.appEmbedded) {
            let appTitle = PP_CONST.APP_THEME[PP_CONST.APP_ACTIVE].BROWSERTITLE + ' - ' + this.queryMeta.title
            document.title = appTitle
          }

          if (this.queryMeta.resultsetCss) {
            let style = document.createElement('style')
            style.innerHTML = this.queryMeta.resultsetCss
            document.getElementById('qryContent_' + this.queryID)
              .appendChild(style)
          }

          if (this.queryMeta.renderModeID === StaticData.QUERY_RENDERMODE.EAPPLICATION_RENDERER && !this.inAppMode && !this.$q.platform.is.mobile) {
            let paramDetailQueryKeyValue = this.detailQueryKeyValue ? this.detailQueryKeyValue : safeParseJSON(history.state.detailQueryKeyValue)

            if (this.popupMode) {
              this.$emit('reloadAppMode', {id: this.queryID, detailQueryKeyValue: paramDetailQueryKeyValue})
              return
            }

            this.$router.replace({
              name: 'QueryApp',
              params: {
                id: this.queryID
              },
              state: {
                detailQueryKeyValue: JSON.stringify(paramDetailQueryKeyValue)
              },
              query: this.$route.query
            }).catch(err => {
            })
            return
          }
          this.init()
        })
        .catch(error => {
          logger.error(error)
          if (typeof this.$t === 'function') {
            this.eventBus.globalErrorHandling(this.$t('queryDefinitionError'), error)
          } else {
            this.eventBus.globalErrorHandling('queryDefinitionError', error)
          }
        })
    }
    ,

    /******************
     * INIT Component
     ******************/
    init() {
      const authStore = useAuthStore()
      const queryStore = useQueryStore()
      // set pagination control for datatable
      if (this.paginationControl.rowsPerPage === 0) {
        this.paginationControl.rowsPerPage = this.queryMeta.resultsetPagesize
      }
      this.chartType = this.queryMeta.chartType
      if (this.rowsPerPageOptions.indexOf(this.queryMeta.resultsetPagesize) === -1) {
        this.rowsPerPageOptions.push(this.queryMeta.resultsetPagesize)
      }

      if (this.paginationActPage !== 0) {
        this.paginationControl.page = this.paginationActPage
      } else {
        if (this.$route.query.page && !this.appEmbedded) {
          try {
            this.paginationControl.page = parseInt(this.$route.query.page)
          } catch (e) {
          }
        }
        if (this.$route.query.pageApp && this.appEmbedded) {
          try {
            this.paginationControl.page = parseInt(this.$route.query.pageApp)
          } catch (e) {
          }
        }
      }

      this.queryParameter = []
      this.showParameterPanel = false
      let paramDetailQueryKeyValue = this.detailQueryKeyValue ? this.detailQueryKeyValue : safeParseJSON(history.state.detailQueryKeyValue)
      //logger.info("!!!!! init Query.vue, paramDetailQueryKeyValue", paramDetailQueryKeyValue)

      // console.log("--- fillqueryparam")
      if (paramDetailQueryKeyValue) {
        // set query parameter from paramDetailQueryKeyValue
        let keyToSelect = paramDetailQueryKeyValue.find(p => p.name === 'k')
        let appKeyToSelect = paramDetailQueryKeyValue.find(p => p.name === 'ak')
        let actionToSelect = paramDetailQueryKeyValue.find(p => p.name === 'ac')
        let appActionToSelect = paramDetailQueryKeyValue.find(p => p.name === 'aac')

        if (keyToSelect != null) {
          authStore.queryKeyFromDeepLink = keyToSelect.value
        }
        if (appKeyToSelect != null) {
          authStore.queryAppKeyFromDeepLink = appKeyToSelect.value
        }
        if (actionToSelect != null) {
          authStore.queryActionNativeNameFromDeepLink = actionToSelect.value
        }
        if (appActionToSelect != null) {
          authStore.queryAppActionNativeNameFromDeepLink = appActionToSelect.value
        }
        // detailQueryKeyValue gets filled from mast/detail call (btnLoadDetailQuery)
        // primary key (from declaration master/detail) and value of the column
        // small version of full cached parameter array
        // format: {name: detailQueryKey, value: detailQueryKeyvalue}
        let url = this.removeURLParameter(this.$route.fullPath, 'page')
        let detailQueryKeyValue = {url: url, paramFields: paramDetailQueryKeyValue, queryID: this.queryID}
        queryStore.addParameterToCache(detailQueryKeyValue)
        this.queryParameter = paramDetailQueryKeyValue
        //console.log("--- this.queryParameter:", this.queryParameter)

      } else {
        // get cached parameter array to this url from store
        // can be short version of mast/detail call (btnLoadDetailQuery)
        let url = this.removeURLParameter(this.$route.fullPath, 'page')
        let detailQueryKeyValue = queryStore.getParameterFromCache(url, this.queryID)
        if (detailQueryKeyValue != null) {
          if (detailQueryKeyValue.paramFields) this.queryParameter = detailQueryKeyValue.paramFields
          else this.queryParameter = detailQueryKeyValue
          if (!detailQueryKeyValue.onlySystemparameter && !authStore.preventParameterInput) {
            this.showParameterPanel = true
          }
        }
      }

      queryStore.loadQryParameter(this.queryID)
        .then((queryParameter) => {
          // logger.info("!!!!!!!!!!!!!!!!!! loadQryParameter 1: " + queryParameter[1].defaultValue + ", " + queryParameter[1].value)
          let hasFormularRefererParam = false

          for (let configParam of queryParameter) {
            if (configParam.requiredMode === true) {
              // required Mode is only true/false when set as sql declare - map to required mode EAGER
              configParam.requiredMode = StaticData.FORM_REQUIRED_MODES.EAGER
            }

            if (configParam.name === "sFormularReferer") hasFormularRefererParam = true

            for (let selectedRow of this.queryParameter) {
              if (configParam.name.toLowerCase() === selectedRow.name.toLowerCase()) {
                configParam.propertyInstanceValue = selectedRow.value
                configParam.value = selectedRow.value
                // logger.info("found and set ", configParam.name, " = ", selectedRow.value)
                break
              }
            }
          }

          // logger.info("----------------- hasFormularRefererParam:", hasFormularRefererParam)
          if (hasFormularRefererParam) {
            let sFormularReferer = sessionStorage.getItem('sFormularReferer')
            // logger.info("Query.vue, ", this.queryMeta.title, " got sFormularReferer: ", sFormularReferer)
            authStore.queryParamListFromDeepLink["sFormularReferer"] = null
            if (sFormularReferer) {
              authStore.queryParamListFromDeepLink["sFormularReferer"] = sFormularReferer
              sessionStorage.removeItem('sFormularReferer');
            }
          }

          this.queryParameter = queryParameter

          if (this.queryParameter != null && this.queryParameter.length > 0) {  // && !this.sideQueryExecDone
            this.queryParameterRefreshCounter++
            this.loading = false
            this.selTab = this.QUERY_TABS.PARAMETER
            this.showParameterPanel = true
            //logger.info("----------- Parameterview should now be visible, this.queryParameter: ", this.queryParameter)

          } else {
            // logger.info("------------ call executeQuery (loadQryParameter)", this.queryMeta.title)
            this.executeQuery()
          }
        })
        .catch(error => {
          let errMsg = this.$t('paramLoadingError')
          this.eventBus.globalErrorHandling(errMsg, error)
        })
    },

    executeQuery(retrieveOnlyKeyRow, keyRowIdx) {
      this.invalidateLazyValueLists()
      if (!this.sideQueryCall && !this.sideQueryExecDone && this.hasSideQuery()) { // sideQueryCall: property from tag, sideQueryExecDone: true after callback, hasSideQuery: if queryMeta has sideQuery configured
        if (this.queryMeta.sideQuery.startsWith('query://')) {
          // Main Query - hasSideQuery triggers call of sideQuery in Desktop.vue
          let sideQueryID = this.queryMeta.sideQuery.substr(8, 38)
          // logger.info('... executeQuery - loadSideQuery in main ', this.queryMeta.title)
          this.eventBus.loadSideQuery(sideQueryID, this.queryID, this.queryParameter)
          return
        }
      }
      const queryStore = useQueryStore()

      // logger.info('... executeQuery: ', this.queryMeta.title)
      //logger.info("... this.sideQueryExecDone: ", this.sideQueryExecDone)

      // logger.info("execQuery ", this.queryID, "queryParameter:", this.queryParameter)
      this.loading = true
      queryStore.execQuery({queryID: this.queryID, queryParameter: this.queryParameter, retrieveOnlyKeyRow, keyRowIdx})
        .then(async (outputMsg) => {

          await this.buildQTableColumns()
          this.buildQTableDataObjects()
          this.registerChangeDeviceOrientationEvent()
          this.buildSmartColFilterArrays()

          //logger.info("this.columns:", this.columns)
          //logger.info("this.queryResult:", this.queryResult)

          await this.$nextTick(async () => {
            this.setAutoRefresh()

            queryStore.viewModeQuery = this.queryMeta.renderModeID
            this.inFullscreen = this.queryMeta.resultsetFullscreenDefault

            if (this.queryMeta.chartType > 0) {
              this.showChartPanel = true
            }

            this.selTab = this.QUERY_TABS.RESULT

            if (this.queryMeta.chartDefaultRun && this.queryResult.length > 0) {
              switch (this.queryMeta.chartType) {
                case 1:
                case 2:
                case 3:
                case 4:
                case 22:
                  this.selTab = this.QUERY_TABS.CHART
                  break
              }
            }

            this.loading = false
            this.queryExecuteDone = true

            await this.$nextTick(() => {
              this.restoreScrollPosition()
              if (!this.inDashboard) this.calcContentHeight()
            })

            this.showOutputMsgFromSQL(outputMsg)

            this.restoreSelection()

            // ...start Autoexec action here:
            await this.loadQueryActionList()

            await this.calcContentHeight()
            this.tableHeightReady = true

            await this.$nextTick(() => {
              if (this.selected.length > 0 && this.selTab === this.QUERY_TABS.RESULT &&
                document.activeElement === document.body) {
                this.$refs.queryTable?.$el?.focus({preventScroll: true})
              }

              if (!this.$q.platform.is.mobile) {
                this.btnDoAPPModeAction(0, false)
              }

              this.autoExecActionFromDeepLink()
            })

            if (this.inAppMode) {
              let action = this.queryActionList.find((action) => action.id === this.queryMeta.resultSetOnClick)
              if (action) {
                let paramList = []
                if (this.selected && this.selected[0]) {
                  for (let key in this.selected[0]) {
                    if (key.startsWith("__")) continue   // internal vue field - ignore it
                    let param = {name: key, value: this.selected[0][key]}
                    paramList.push(param)
                  }
                }
              }
            }

            this.autoSkipParameter = false
            this.$emit('execQueryExecuteDone')
            this.$q.loading.hide()
          })
        })
        .catch(error => {
          this.eventBus.globalErrorHandling(this.$t('queryExecutionError'), error)
          this.loading = false
          this.queryExecuteDone = true
        })

      this.$emit('refreshFavList')
    },

    parameterEntered(paramFields) {
      const authStore = useAuthStore()
      const queryStore = useQueryStore()
      this.queryParameter = paramFields.paramFields
      //logger.info("Query - parameterEntered", this.queryMeta.title, paramFields)
      //logger.info("Query - parameterEntered", this.queryParameter)

      if (paramFields.invalid) {
        this.queryMeta.skipParamsOnDefault = false
        return
      }

      let url = this.removeURLParameter(this.$route.fullPath, 'page')
      let params = {url: url, paramFields, queryID: this.queryID}

      // forcre refresh of parameter component (key)
      //this.queryParameterRefreshCounter++  // todo: check this out!
      this.columns = []
      //this.queryResult = []
      this.secondLineColumns = []

      // hack: no parameter caching (used for master/detail queries to go back) for embedded (Dashboard) - cause the URL for all Dashbaord panels
      // would be the same and we have a problem
      queryStore.addParameterToCache(params)
      if (paramFields.onlySystemparameter || authStore.preventParameterInput) {
        this.showParameterPanel = false
      }

      // logger.info("call executeQuery (parameterEntered)", this.queryMeta.title)
      this.queryExecuteDone = false
      this.executeQuery()
    },

    closeSingleRecordView() {
      this.selTab = this.QUERY_TABS.RESULT
      this.loading = false
      this.reload()
    },

    async loadQueryActionList() {
      logger.info('... getActionList')
      const queryStore = useQueryStore()
      let hasAutorunAction = false
      try {
        this.queryActionList = await queryStore.loadQryActionList(this.queryID)
        // autorun action
        for (let i = 0; i < this.queryActionList.length; i++) {
          let action = this.queryActionList[i]

          if (action.autorun) {

            // console.log('... autorun action found: ', action.name, action.type)

            if (action.type === 1) {
              // action type 'Query'
              // as_done flag indicates that last run was same action. no endless loop in case of same query (e.g. action is sql with sp)
              if (!localStorage.getItem("as_done_" + action.id)) {
                logger.info("Start Action autorun for '" + action.title + "'")
                await this.btnDoQryAction(action, true)
                localStorage.setItem("as_done_" + action.id, "true")

              } else {
                localStorage.removeItem("as_done_" + action.id)
              }

            } else {
              logger.info("Start Action autorun for '" + action.title + "'")
              await this.btnDoQryAction(action, true)
            }
            break
          }
          if (action.predefNew) {
            this.floatingAddAction = action
          }
        }

      } catch (error) {
        this.eventBus.globalErrorHandling(this.$t('actionListError'), error)
        this.loading = false
      }
      //return hasAutorunAction
    },

    removeLocalStorageWithPrefix(prefix) {
      var arr = []; // Array to hold the keys
      // Iterate over localStorage and insert the keys that meet the condition into arr
      for (var i = 0; i < localStorage.length; i++) {
        if (localStorage.key(i).substring(0, 8) === prefix) {
          arr.push(localStorage.key(i));
        }
      }

      // Iterate over arr and remove the items by key
      for (var i = 0; i < arr.length; i++) {
        localStorage.removeItem(arr[i])
      }
    }
    ,

    removeURLParameter(url, parameter) {
      //prefer to use l.search if you have a location/link object
      let urlparts = url.split('?')
      if (urlparts.length >= 2) {

        let prefix = encodeURIComponent(parameter) + '='
        let pars = urlparts[1].split(/[&;]/g)

        //reverse iteration as may be destructive
        for (let i = pars.length; i-- > 0;) {
          //idiom for string.startsWith
          if (pars[i].lastIndexOf(prefix, 0) !== -1) {
            pars.splice(i, 1)
          }
        }

        url = urlparts[0] + (pars.length > 0 ? '?' + pars.join('&') : "")
        url = url.replace("%7B", "").replace("%7D", "").replace("{", "").replace("}", "")
        return url;
      } else {
        return url
      }
    }
    ,

    async buildQTableColumns() {
      const queryStore = useQueryStore()
      this.columns = []
      this.valueListOptions = {}
      this.invalidateLazyValueLists()
      this.vlFilteredOptions = null
      if (queryStore.queryColumns.length === 0) {
        return
      }
      if (this.queryMeta.detailqueryID) {
        let colMasterQueryBtnAction = {
          name: "action",
          field: "action",
          newCol: "action",
          id: 9999,
          width: "30px",
          required: true
        }
        this.columns.push(colMasterQueryBtnAction)
      }

      if (this.queryMeta.recordiconClass) {
        let colRecordIcon = {
          name: "recordIcon",
          field: "recordIcon",
          newCol: "recordIcon",
          id: 9998,
          width: "20px",
          required: true,
          icon: this.queryMeta.recordiconClass
        }
        this.columns.push(colRecordIcon)
      }

      if (this.queryMeta.allowRecordReorder) {
        let colAllowRecordReorder = {
          name: "allowRecordReorder",
          field: "allowRecordReorder",
          newCol: "allowRecordReorder",
          id: 9997,
          required: true,
          width: "80px",
        }
        this.columns.push(colAllowRecordReorder)
      }

      // check if one cell has value "<hide/>" - if so, suppress the column
      let skipCols = []
      if (queryStore.queryResultRows.length > 0 && queryStore.queryResultRows[0].cell) {
        for (let i = 0; i < queryStore.queryResultRows[0].cell.length; i++) {
          //logger.info("col value:", queryStore.queryResultRows[0].cell[i])
          if (queryStore.queryResultRows[0].cell[i] == '<hide/>') {
            skipCols.push(i)
          }
        }
      }

      // build column object for q-table
      for (let i = 0; i < queryStore.queryColumns.length; i++) {
        let col = queryStore.queryColumns[i]
        if (skipCols.includes(i)) {
          col.columnWidth = 0
        }

        let align = ""
        switch (col.projectionHalign) {
          case 0:
            align = "center";
            break;
          case 1:
            align = "right";
            break;
          case 2:
          default:
            align = "left";
        }
        let newCol = {}

        newCol.name = col.name
        newCol.nativename = col.nativename || col.name
        newCol.datatypeID = col.dataTypeID
        newCol.label = col.title
        newCol.field = col.name
        newCol.id = col.ID === "null" ? "" : col.ID
        newCol.width = col.columnWidth === -1 ? "auto" : col.columnWidth + "px"
        newCol.headerStyle = col.columnWidth === -1 ? "width:auto" : "min-width:" + col.columnWidth + "px"
        if (newCol.datatypeID === StaticData.DATATYPES.TYPE_BOOLEAN) {
          newCol.headerStyle = ";text-align:center"
        }
        newCol.sortable = true
        newCol.projectionMobileview = col.projectionMobileview
        newCol.align = align
        if (newCol.datatypeID === StaticData.DATATYPES.TYPE_FLOAT || newCol.datatypeID === StaticData.DATATYPES.TYPE_DOUBLE || newCol.datatypeID === StaticData.DATATYPES.TYPE_INT) {
          newCol.align = 'right'
        }
        newCol.readonly = !col.projectionUpdateable
        newCol.primaryKey = col.primaryKey
        newCol.lengthMax = col.lengthMax
        newCol.lengthMin = col.lengthMin
        newCol.valueMin = col.valueMin
        newCol.valueMax = col.valueMax
        newCol.projectionEappLayoutpos = col.projectionEappLayoutpos
        newCol.projectionSecondline = col.projectionSecondline
        newCol.detailviewHidden = col.detailviewHidden
        newCol.valuelistType = col.valuelistType
        newCol.projectionValuelist = col.projectionValuelist
        newCol.projectionValuelistLazyLoading = col.projectionValuelistLazyLoading === true
        newCol.projectionUpdateable = col.projectionUpdateable
        newCol.chartColor = col.chartColor
        newCol.chartIsLegend = col.chartIsLegend
        newCol.projectionIsGroupCol = col.projectionIsGroupCol
        newCol.projectionExport = col.projectionExport
        newCol.projectionRequestParam = col.projectionRequestParam
        newCol.projectionCommaPre = col.projectionCommaPre
        newCol.projectionCommaPost = col.projectionCommaPost
        newCol.projectionMicrochartType = col.projectionMicrochartType
        newCol.projectionMicrochartFullValue = col.projectionMicrochartFullValue
        newCol.projectionChipType = col.projectionChipType
        newCol.projectionChipColor = col.projectionChipColor
        newCol.projectionCSS = col.projectionCSS
        newCol.projectionPostUpdateBehaviour = col.projectionPostUpdateBehaviour
        newCol.projectionCSSFieldClass = col.projectionCSSFieldClass
        newCol.projectionValuelistStrict = col.projectionValuelistStrict
        newCol.nullValueReplacement = col.nullValueReplacement
        newCol.projectionSmartCol = col.projectionSmartCol
        newCol.hasBLOBDwldStmt = col.hasBLOBDwldStmt
        newCol.projectionValuelistWithDependencies = col.projectionValuelistWithDependencies

        this.filter[newCol.name] = ""

        if (newCol.projectionIsGroupCol) {
          this.groupColName = newCol.name
          this.groupedView = true
        }

        if (col.primaryKey) {
          this.queryKey = newCol.name
        }   // best case: a real key for the rows

        if (newCol.datatypeID === StaticData.DATATYPES.TYPE_VALUELIST) {
          if (newCol.projectionUpdateable) {
            if (newCol.projectionValuelist && (newCol.valuelistType !== 2 || !newCol.projectionValuelistLazyLoading) &&
                !newCol.projectionValuelistWithDependencies) {
              this.valueListOptions[newCol.name] = []

              // logger.info("valuelist: ", newCol.title, newCol.projectionValuelist)

              // check for this: eins;zwei;drei=eins;zwei;drei (happen when SQL result gives us csv list
              let valueParts = newCol.projectionValuelist.split("=")
              if (valueParts.length === 2 && valueParts[0] === valueParts[1] && valueParts[0].indexOf(";") !== -1) {
                newCol.projectionValuelist = valueParts[0]
              }

              let optionItems = newCol.projectionValuelist.split(";")
              optionItems.forEach(x => {
                if (x.includes("=")) {
                  let optArray = x.split("=")
                  this.valueListOptions[newCol.name].push({label: optArray[0], value: optArray[1]})
                } else {
                  this.valueListOptions[newCol.name].push({label: x, value: x})
                }
              })
            }
          }
        }

        newCol.format = function (value, row) {
          return value
        }

        if (col.projectionUpdateable) {
          this.isUpdateable = true
        }

        if (newCol.datatypeID === StaticData.DATATYPES.TYPE_INT) {
          newCol.sort = (a, b) => {
            let pa = parseInt(a, 10)
            let pb = parseInt(b, 10)
            if (pa && pb) {
              return pa - pb
            } else {
              if (pa) {
                return -1
              }
              if (pb) {
                return 1
              }
              return 0
            }
          }
        }

        if (newCol.datatypeID === StaticData.DATATYPES.TYPE_FLOAT || newCol.datatypeID === StaticData.DATATYPES.TYPE_DOUBLE) {
          newCol.sort = (a, b) => {
            let pa = parseFloat(a)
            let pb = parseFloat(b)
            if (pa && pb) {
              return pa - pb
            } else {
              if (pa) {
                return -1
              }
              if (pb) {
                return 1
              }
              return 0
            }
          }
        }

        if (newCol.datatypeID === StaticData.DATATYPES.TYPE_DATE || newCol.datatypeID === StaticData.DATATYPES.TYPE_DATETIME) {
          newCol.sort = (a, b) => {
            let dateA = moment(a, ["YYYY/MM/DD HH:mm:ss", "DD.MM.YYYY HH:mm:ss", "YYYY/MM/DD HH:mm", "DD.MM.YYYY HH:mm", "YYYY/MM/DD", "DD.MM.YYYY"])
            let dateB = moment(b, ["YYYY/MM/DD HH:mm:ss", "DD.MM.YYYY HH:mm:ss", "YYYY/MM/DD HH:mm", "DD.MM.YYYY HH:mm", "YYYY/MM/DD", "DD.MM.YYYY"])
            if (dateA.isAfter(dateB)) {
              return 1
            } else if (dateA.isBefore(dateB)) {
              return -1
            } else {
              return 0
            }
          }
        }

        if (col.projectionSecondline) {
          this.secondLineColumns.push(col)

        } else {
          if (newCol.width !== '0px') {
            this.availableColumns.push(newCol)
          }
        }

        this.columns.push(newCol)
        if (newCol.primaryKey) {
          this.primaryKeyName = newCol.name
        }
      }

      // generate a new col for the unique index of q-table
      let newCol = {}
      newCol.name = "__key__"
      newCol.label = ""
      newCol.field = newCol.name
      this.columns.push(newCol)
      this.rowKey = newCol.name

      if (this.secondLineColumns.length > 0) {
        this.cssRow = "cRow"
      }

      if ((window.screen.orientation && window.screen.orientation.type === "landscape-primary") ||
        (window.orientation && Math.abs(window.orientation) == 90)) {
        this.orientation = "landscape"
      }

      this.visibleColumns = this.getVisibleColumnsForOrientation(this.orientation, this.columns)

      this.tableColSortConfig = null
      this.$nextTick(() => {
        if (this.queryMeta.allowColumnReorder) {
          this.tableColSortConfig = this.getTableColSortConfig()
        }
      })
    }
    ,

    buildQTableDataObjects() {
      const queryStore = useQueryStore()
      this.queryResult = []

      // console.log("queryStore.queryResultRows:", queryStore.queryResultRows)

      this.$nextTick(() => {
        // build data object for q-table. Each column from below read is a row in this object.
        for (let i = 0; i < queryStore.queryResultRows.length; i++) {
          let element = queryStore.queryResultRows[i]
          let row = {}
          let offset = 0
          if (this.queryMeta.detailqueryID) offset++
          if (this.queryMeta.allowRecordReorder) offset++
          if (this.queryMeta.recordiconClass) offset++

          for (let x = 0; x < this.columns.length; x++) {
            let colName = this.columns[x].field

            let value = ""
            if (colName === "__key__") value = ("i" + i)
            else {
              value = element.cell[x]

              if (x >= offset) {
                value = element.cell[x - offset]
              } else {
                value = "";  // value of action column (first column)
              }
            }

            if ((this.columns[x].datatypeID === StaticData.DATATYPES.TYPE_FLOAT || this.columns[x].datatypeID === StaticData.DATATYPES.TYPE_DOUBLE) && this.columns[x].projectionUpdateable) {
              if (value && ("" + value).includes(',')) value = ("" + value).replace(',', '.')
            }

            row[colName] = value

            if (this.queryMeta.renderModeID === StaticData.QUERY_RENDERMODE.EAPPLICATION_RENDERER && this.columns[x].projectionEappLayoutpos !== 0) {
              switch (this.columns[x].projectionEappLayoutpos) {
                case 1: // left top
                  row["leftTop"] = value
                  row["cssleftTop"] = this.getProjCssEAPP(this.columns[x])
                  row["chipleftTop"] = this.getChipEAPP(this.columns[x])
                  break
                case 2: // left
                  row["left"] = value
                  row["cssleft"] = this.getProjCssEAPP(this.columns[x])
                  row["chipleft"] = this.getChipEAPP(this.columns[x])
                  break
                case 3: // left bottom
                  row["leftBottom"] = value
                  row["cssleftBottom"] = this.getProjCssEAPP(this.columns[x])
                  row["chipleftBottom"] = this.getChipEAPP(this.columns[x])
                  break
                case 4: // right top
                  row["rightTop"] = value
                  row["cssrightTop"] = this.getProjCssEAPP(this.columns[x])
                  row["chiprightTop"] = this.getChipEAPP(this.columns[x])
                  break
                case 5: // right
                  row["right"] = value
                  row["cssright"] = this.getProjCssEAPP(this.columns[x])
                  row["chipright"] = this.getChipEAPP(this.columns[x])
                  break
                case 6: // right bottom
                  row["rightBottom"] = value
                  row["cssrightBottom"] = this.getProjCssEAPP(this.columns[x])
                  row["chiprightBottom"] = this.getChipEAPP(this.columns[x])
                  break
                case 7: // avatar icon fontname
                  row["avatarIcon"] = value
                  row["cssavatarIcon"] = this.getProjCssEAPP(this.columns[x])
                  break
                case 8: // info
                  row["avatarText"] = value
                  row["cssavatarText"] = this.getProjCssEAPP(this.columns[x])
                  break
              }
            }
          }
          this.queryResult.push(row)
        }

        if (this.groupedView) {
          this.queryResult.sort((a, b) => this.queryResultSort(a, b))
        }

        if (this.queryMeta.msgOnEmptyresult) {
          this.noDataLabel = this.queryMeta.msgOnEmptyresult
        }
        //this.$emit("queryExecDone")

        if (this.visibleColumns.length === 0 && queryStore.queryResultRows.length > 0) {
          this.$q.notify({
            type: "warning",
            message: this.$t('noVisibleColumnsError')
          })
        }
      })
    },

    replaceParameterPlaceholder(value) {
      try {
        if (value && this.queryParameter && this.queryParameter.length > 0) {
          for (let i = 0; i < this.queryParameter.length; i++) {
            const param = this.queryParameter[i]
            if (param.value) {
              value = value.replaceAll(":" + param.name, param.value)
            }
          }
          // Nur restliche Platzhalter entfernen, wenn wir tatsächlich Parameter hatten
          value = CSVHelper.removeWordsWithColon(value)
        }
      } catch (e) {
        logger.info("replaceParameterPlaceholder err", e)
      }
      return value
    },

    buildSmartColFilterArrays() {
      let smartCols = this.columns.filter(c => c.projectionSmartCol)
      if (smartCols.length > 0) {
        smartCols.forEach(col => {
          this.filterArrays[col.name] = []
        })

        for (let i = 0; i < this.queryResult.length; i++) {
          let row = this.queryResult[i]
          smartCols.forEach(col => {
            if (this.filterArrays[col.name].length < 35 && !this.filterArrays[col.name].includes('' + row[col.name])) {
              this.filterArrays[col.name].push('' + row[col.name])
            }
          })
        }
      }
    }
    ,

    hasSideQuery() {
      return this.queryMeta.sideQuery && this.queryMeta.sideQuery.trim() != ""
    }
    ,

    registerChangeDeviceOrientationEvent() {
      if (!this.$q.platform.is.mobile) {
        return
      }
      var mediaQuery = window.matchMedia('(orientation:landscape)')
      var vm = this
      let checkDeviceOrientation = function (mediaQuery) {
        if (mediaQuery.matches) {
          vm.eventBus.changeDeviceOrientationLandscape()
        } else {
          vm.eventBus.changeDeviceOrientationPortrait()
        }
      }
      mediaQuery.addListener(checkDeviceOrientation)
    }
    ,

    onEvtChangeDeviceOrientation(orientation) {
      this.visibleColumns = this.getVisibleColumnsForOrientation(orientation, this.columns)
    }
    ,

    getVisibleColumnsForOrientation(orientation, columns) {
      let result = []
      if (this.$q.platform.is.mobile) {
        columns.forEach((col) => {
          if (col.projectionSecondline) {
            return
          }
          if (col.name == '__key__') {
            return
          }
          if (col.width == '0px') {
            return
          }

          if (orientation === 'landscape') {
            switch (col.projectionMobileview) {
              case -1: // not declared
              case 1:  // always
              case 3:
                result.push(col.name)
                break
            }
          } else {
            switch (col.projectionMobileview) {
              case -1:
              case 1:
              case 2:
                result.push(col.name)
                break
            }
          }
        })

      } else {
        columns.forEach((col) => {
          if (col.projectionSecondline) {
            return
          }
          if (col.name == '__key__') {
            return
          }
          if (col.width == '0px') {
            return
          }
          result.push(col.name)
        })
      }

      let localStorageInvisibleColNames = localStorage.getItem(this.queryID + "__invisibleColumns")
      if (localStorageInvisibleColNames && localStorageInvisibleColNames != "") {
        let localStorageInvisibleCols = localStorageInvisibleColNames.split(",")
        result = result.filter(vCol => (!localStorageInvisibleCols.includes(vCol)))
      }
      return result
    },

    getVisibleColumnsForTable() {
      let result = []
      if (this.visibleColumns.length > 0) {
        result = [...this.visibleColumns]
        result.push(this.primaryKeyName)
      }
      return result
    },

    calcContentHeight() {
      return this.$nextTick(() => {
        const dialogHeight = this.popupMode ? dialogAvailableHeight(this.$el) : null
        if (this.dialogScroll && dialogHeight !== null) {
          // Measure this view's actual flow, not global IDs: caller and popup may share a query ID.
          const table = this.$el.querySelector('.my-sticky-header-table')
          const panels = this.$el.querySelector('.q-tab-panels')
          const anchor = table || panels
          const header = anchor ? Math.max(0, anchor.getBoundingClientRect().top - this.$el.getBoundingClientRect().top) : 0
          const height = Math.max(0, dialogHeight - header)
          if (this.contentHeight !== height) this.contentHeight = height
          return
        }
        let wndHeight = window.innerHeight
        let offset = 71
        let qryHeadHeight = 0
        let qryToolbar = 3
        let volatileParamBar = 0
        let dashboardMaximizedOffset = 0
        let dashboardHeaderMaximizedOffset = 0

        if (this.dashboardMaximized) {
          dashboardMaximizedOffset = 10
          if (this.dashboardHeaderProp) {
            dashboardHeaderMaximizedOffset = 110
          }
        }

        if (this.embedded) {
          try {
            wndHeight = document.getElementById('qryContent_' + this.queryID).parentNode.clientHeight - 1
          } catch (e) {
            //console.error("Error getting wndHeight for embedded mode")
          }

          try {
            if (document.getElementById('qryContent_' + this.queryID)
              .parentNode
              .className
              .includes('q-splitter')) {
              offset = 0
            } else {
              offset = 17
            }
          } catch (e) {
            offset = 17
            //console.error("Error getting wndHeight for embedded mode")
          }
        }

        if (dialogHeight !== null) {
          wndHeight = dialogHeight
          offset = 0
        }

        try {
          qryHeadHeight = document.getElementById("qryHead_" + this.queryID).offsetHeight
        } catch (e) {
          //console.error("error get offsetHeight of qryHeadHeight")
        }

        try {
          if (!this.inFullscreen) {
            qryToolbar = document.getElementById("qryToolbar_" + this.queryID).offsetHeight
          }
        } catch (e) {
          //console.error("error get offsetHeight of qryToolbar")
        }

        try {
          volatileParamBar = this.showParameterPanel && (this.selTab === this.QUERY_TABS.RESULT || this.selTab === this.QUERY_TABS.CHART) ? document.getElementById("volatileParamBar_" + this.queryID).offsetHeight : 0
        } catch (e) {
          //console.error("error get offsetHeight of volatileParamBar")
        }

        const height = wndHeight - qryHeadHeight - qryToolbar - volatileParamBar - offset - dashboardMaximizedOffset - dashboardHeaderMaximizedOffset
        const available = dialogHeight === null ? height : Math.max(0, height)
        if (this.contentHeight !== available) this.contentHeight = available
        //logger.info("calcContentHeiht ", this.queryMeta.title, this.contentHeight)
      })
    }
    ,

    gmapPOI(lat, lng, title) {
      this.eventBus.gmapPOI(lat, lng, title)
    }
    ,

    btnGantt() {
      //logger.info("this.queryMeta:", this.columns)
      this.$router.push({
        name: 'Gantt',
        state: {
          id: this.queryID,
          data: {
            chartType: this.queryMeta.chartType,
            queryColumns: JSON.stringify(this.columns),
            queryResult: JSON.stringify(this.queryResult)
          }
        }
      })
        .catch(err => {
        })
    }
    ,

    onRefreshComp(data) {
      if (data.nativename === StaticData.ROUTE_NAMES.QUERY &&
        data.value === this.queryID) {
        this.sideQueryExecDone = data.sideQueryExecDone ? data.sideQueryExecDone : false
        this.queryParameter.autoSkipParamsDone = false
        //console.warn("onRefreshComp", data)
        this.reload()
      }
    }
    ,

    queryResultSort(a, b) {
      if (a[this.groupColName] != null && b[this.groupColName] != null) {
        return a[this.groupColName].localeCompare(b[this.groupColName])
      } else if (a[this.groupColName] == null && b[this.groupColName] != null) {
        return -1
      } else if (a[this.groupColName] != null && b[this.groupColName] == null) {
        return 1
      } else {
        return 0
      }
    }
    ,

    showExport(filetype) {
      const authStore = useAuthStore()
      if (authStore.queryExportOptions.exportTypes && authStore.queryExportOptions.exportTypes.includes(filetype)) {
        return true
      }
      return false
    },

    refreshDetailView() {
      this.removeLocalStorageWithPrefix("as_done_");
      this.reload()
    },

    onKeydown(e) {
      if ((e.code === 'Space' || e.key === ' ') && !e.defaultPrevented && !e.repeat &&
        !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        const target = e.target instanceof Element ? e.target : null
        const action = this.queryActionList.find(action => action.id === this.queryMeta.resultSetOnDoubleClick)
        if (this.selTab === this.QUERY_TABS.RESULT && this.selected.length === 1 &&
          this.$el.offsetParent !== null && !document.querySelector('.q-dialog') &&
          !target?.closest('input, textarea, select, button, [contenteditable="true"], [role="textbox"]') &&
          action?.nativename === 'acDownload' && (action.type === 1 || action.type === 4)) {
          e.preventDefault()
          e.stopImmediatePropagation()
          if (!this.quickLookPending) {
            this.quickLookPending = true
            this.btnDoQryAction(action, true, true).finally(() => {
              this.quickLookPending = false
            })
          }
          return
        }
      }
      if (["ArrowUp", "ArrowDown"].includes(e.key) && this.navigationActive) {
        let direction = e.key === "ArrowUp" ? "up" : "down"
        this.moveTableRowSelection(direction)
      }
    },

    moveTableFieldSelection(direction) {  // direction : "left" or "right"
      let selectedRow = this.selected.length === 0 ? null : this.selected[0]
      if (selectedRow && this.focusedTableColName) {
        const focusedColIndex = this.visibleColumns.indexOf(this.focusedTableColName)
        const remainingCols = direction === "right" ? this.visibleColumns.slice(focusedColIndex) : this.visibleColumns.slice(0, focusedColIndex).reverse()
        const nextInputCol = remainingCols.find(col => {
          const colInput = this.$refs[`${selectedRow[this.rowKey]}_${col}`]
          return col !== this.focusedTableColName && colInput && colInput.length > 0
        })
        if (nextInputCol) {
          const colInput = this.$refs[`${selectedRow[this.rowKey]}_${nextInputCol}`][0]
          colInput.focus()
          setTimeout(() => colInput.select(), 0)
          this.focusedTableColName = nextInputCol
        }
      }
    },

    moveTableRowSelection(direction) { // direction : "up" or "down"
      let selectedRow = this.selected.length == 0 ? null : this.selected[0]
      const tableRows = this.$refs.queryTable.filteredSortedRows
      if (!selectedRow) {
        this.selectRow(tableRows[0])
      } else {
        const index = tableRows.findIndex(res => res == selectedRow)
        let nextIndex
        if (direction == "up") {
          nextIndex = index - 1
          if (nextIndex < 0 || nextIndex % this.paginationControl.rowsPerPage == this.paginationControl.rowsPerPage - 1) {
            return
          }
        } else {
          nextIndex = index + 1
          if (nextIndex != 0 && nextIndex % this.paginationControl.rowsPerPage == 0) {
            return
          }
        }
        this.selectRow(tableRows[nextIndex])
        // if a table field input was focused in the previous row, focus the corresponding field in current row
        if (this.focusedTableColName) {
          selectedRow = this.selected[0]
          const colInput = this.$refs[`${selectedRow[this.rowKey]}_${this.focusedTableColName}`][0]
          if (colInput) {
            colInput.focus()
            setTimeout(() => colInput.select(), 0)
          }
        }
      }
      if (this.selected) {
        this.btnDoAPPModeAction(0, true)
      }
    }
    ,

    onDragTableHeader(e) {
      const {element, newIndex} = e
      const oldIndex = this.tableColSortConfig[element.name]
      let targetColumn = this.columns.filter(col => this.getVisibleColumnsForTable().includes(col.name) && col.width != "0px")[newIndex]
      let actualNewIndex = this.tableColSortConfig[targetColumn.name]
      this.moveTableCol(element.name, actualNewIndex, oldIndex)
    }
    ,

    moveTableCol(movedColumnName, newIndex, oldIndex) {
      let tableSortConfig = {...this.tableColSortConfig}
      const tableHeadersSorted = Object.entries(tableSortConfig).sort(([, index1], [, index2]) => index1 - index2)
      const movedHeaderConfig = tableHeadersSorted.find(thconfig => thconfig[0] == movedColumnName)
      const movedForth = newIndex - oldIndex > 0
      for (let i = 0; i < tableHeadersSorted.length; i++) {
        const th = tableHeadersSorted[i]
        let thIndex = th[1]
        if (i == oldIndex) {
          continue
        }
        if (movedForth) {
          if (i > oldIndex && i <= newIndex) {
            if (i == newIndex) {
              movedHeaderConfig[1] = thIndex
            }
            --thIndex
          }
        } else {	// moved back
          if (i >= newIndex && i <= oldIndex) {
            if (i == newIndex) {
              movedHeaderConfig[1] = thIndex
            }
            ++thIndex
          }
        }
        tableHeadersSorted[i][1] = thIndex
      }
      tableSortConfig = tableHeadersSorted.reduce((r, [k, v]) => ({...r, [k]: v}), {})
      this.saveTableColSortConfig(tableSortConfig)
    }
    ,

    getTableColSortConfig() {
      let jsonConfig = localStorage.getItem(`${this.queryID}__tableSortConfig`)
      let config = null
      if (!jsonConfig) {
        this.saveTableColSortConfig()
        jsonConfig = localStorage.getItem(`${this.queryID}__tableSortConfig`)
        config = JSON.parse(jsonConfig)
      } else {
        config = JSON.parse(jsonConfig)
        // add any missing columns to the sort config:
        for (let i = 0; i < this.columns.length; i++) {
          const header = this.columns[i]
          let headerIndex = config[header.name]
          if (headerIndex == null) {
            config[header.name] = Math.max(Object.values(config)) + 1
          }
        }
        this.saveTableColSortConfig(config)
      }
      return config
    }
    ,

    saveTableColSortConfig(config) {
      let tableSortConfig = config
      if (!tableSortConfig) {
        tableSortConfig = {}
        for (let i = 0; i < this.columns.length; i++) {
          const header = this.columns[i]
          tableSortConfig[header.name] = i
        }
      }
      localStorage.setItem(`${this.queryID}__tableSortConfig`, JSON.stringify(tableSortConfig))
      this.tableColSortConfig = tableSortConfig
    }
    ,

    getProjCSS(col, row) {
      //if (!this.selected.includes(row)) {
      if (col.projectionCSS) {
        return col.projectionCSS
      }
      if (col.projectionCSSFieldClass) {
        return row[col.projectionCSSFieldClass]
      }
      //}
    }
    ,

    getProjCssEAPP(col) {
      if (col.projectionCSS) {
        return col.projectionCSS
      }
      if (col.projectionCSSFieldClass) {
        return row[col.projectionCSSFieldClass]
      }
    }
    ,

    getChipEAPP(col) {
      let result = null
      if (col.projectionChipType) {
        result = {
          square: col.projectionChipType == 1 ? true : false,
          outline: col.projectionChipType == 2 ? true : false,
          textColor: col.projectionChipType != 2 ? 'white' : '',
          type: col.projectionChipType,
          colorCol: col.projectionChipColor
        }
      }
      return result
    }
    ,

    getChipEAPPStyle(type, color) {
      let style = 'width: fit-content; '
      style += type == 2 ? 'color:' + color + '; border: 1px solid; border-color:' + color : 'background-color:' + color
      return style
    }
    ,

    getProjACIcon(acProjString) {
      let result = ""
      if (acProjString && acProjString.indexOf("ico") !== -1) {
        let icoPos = acProjString.indexOf("ico") + 4
        if (icoPos !== -1) {
          result = acProjString.substr(icoPos)
        }
      }
      return result
    },

    getProjACText(acProjString) {
      let result = ""
      if (acProjString && acProjString.indexOf("ico") == -1) {
        result = acProjString.substring(acProjString.indexOf(';') + 1)
      }
      return result
    },

    isNumber(value) {
      if (isNaN(value) || !isFinite(value)) return false
      return true
    },

    safeDebug(obj) {
      const seen = new WeakSet()
      const str = JSON.stringify(obj, (key, value) => {
        // Vue-interne Properties überspringen
        if (key.startsWith('__v_') || key === 'dep' || key === 'deps' || key === 'sub') {
          return undefined
        }
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) return '[Circular]'
          seen.add(value)
        }
        return value
      }, 2)
      return str
    }
  },

  beforeUnmount() {
    this.actionResultCallerUnmounted = true
    this.eventBus.off('refreshDetailView', this.refreshDetailView)
    this.eventBus.off('onExportQuery', this.exportQuery)
    this.eventBus.off('onExecuteAndExportQuery', this.onExecuteAndExportQuery)
    this.eventBus.off('resizeContentWindow', this.calcContentHeight)
    this.eventBus.off('onRefreshComp', this.onRefreshComp)
    this.eventBus.off('onQueryRowSel', this.queryRowSelEventReceiver)
    this.eventBus.off('evtChangeDeviceOrientation', this.onEvtChangeDeviceOrientation)
    this.eventBus.off('queryBackBtn', this.queryBackBtn)

    clearInterval(this.autorefreshIntervallHandler)
    window.removeEventListener('resize', this.calcContentHeight)
    window.removeEventListener('keydown', this.onKeydown)
  },

  mounted() {
    const authStore = useAuthStore()
    const queryStore = useQueryStore()

    /*
        let store = this.safeDebug(queryStore.getSelQueryRowAll())

        this.$q.notify({
          message: this.queryID + " : " + store,
          position: 'top',
          timeout: 2000
        })
    */

    if (this.clear) {
      queryStore.clearSelQueryRow({queryID: this.queryID})
    }
    window.addEventListener('resize', this.calcContentHeight)
    window.addEventListener('keydown', this.onKeydown)

    this.eventBus.on('refreshDetailView', this.refreshDetailView)
    this.eventBus.on('onExportQuery', this.exportQuery)
    this.eventBus.on('onExecuteAndExportQuery', this.onExecuteAndExportQuery) // new implemententation: generice exporter plugin (configured in siteproperties)
    this.eventBus.on('resizeContentWindow', this.calcContentHeight)
    this.eventBus.on('onRefreshComp', this.onRefreshComp)
    this.eventBus.on('onQueryRowSel', this.queryRowSelEventReceiver)
    this.eventBus.on('evtChangeDeviceOrientation', this.onEvtChangeDeviceOrientation)
    this.eventBus.on('queryBackBtn', this.queryBackBtn)

    if (PP_CONST.APP_THEME[PP_CONST.APP_ACTIVE].COPY_CLIPBOARD_ALLOWED_USER) {
      let allowedUser = PP_CONST.APP_THEME[PP_CONST.APP_ACTIVE].COPY_CLIPBOARD_ALLOWED_USER.split(";")
      if (PP_CONST.APP_THEME[PP_CONST.APP_ACTIVE].COPY_CLIPBOARD_ALLOWED_USER === "*" || allowedUser.includes(authStore.loginname)) this.copyClipboardAllowed = true
    }

    // logger.info("angemeldet: ", PP_CONST.APP_THEME[PP_CONST.APP_ACTIVE].COPY_CLIPBOARD_ALLOWED_USER)

    this.removeLocalStorageWithPrefix("as_done_")

    this.reload()
  }
}


</script>

<style lang="sass">

// sticky CSS for query display:  in full size mode
// more top, so the toolbar considered
.stickyFullsize,
.stickyTop
  position: sticky
  top: 35px
  padding-top: 4px
  width: 100%

  .actions
    padding: 10px 0 5px 0

.stickyTop
  top: -19px

.embedViewMode
  border: 1px solid
  border-color: var(--q-color-primary)

.txtMobileFilter
  width: 100%


.my-card
  width: 100%
  max-width: 250px
  transition: all .3s ease-in-out

.my-card:hover
  transform: scale(1.05)

//@media (min-width: 600px)
//  .my-card
//    max-width: 200px


h3
  font-size: 2em
  line-height: 1.2em
  margin: 0

.embedded
  h3
    margin-top: 5px
    font-size: 1.3em
    margin-left: 0
    margin-bottom: 0 !important
    font-weight: bold

  .q-table
    th:first-child,
    td:first-child
      padding-left: 0px !important


body.screen--xs
  h3
    font-size: 1.2em
    line-height: 1.3em
    margin-bottom: 3px

.q-field__append
  padding-left: 0 !important

.q-field__prepend
  padding-right: 0 !important
  margin-right: -5px

.q-table tbody tr.selected
  // background: $amber-3
  background-color: rgba(255, 165, 0, 0.5) !important

.q-btn-inner img
  height: 20px


.q-table

  th
    font-weight: bold

    font-size: 13px

  td
    white-space: pre-line

  th:first-child,
  td:first-child
    padding-left: 5px !important

  .embedded
    th:first-child,
    td:first-child
      padding-left: 0 !important


  .secLine
    color: #AAAAAA
    height: 23px !important

    td
      padding-top: 0
      height: 23px !important

    tr
      height: 23px !important

  .secLineNoBorder
    height: 23px !important

    td
      border-bottom: none
      padding-bottom: 0
      height: 23px !important

  .cRowL
    cursor: pointer
    border-bottom: 1px solid #DDDDDD

  .cRowL td
    padding-top: 7px
    padding-bottom: 7px

  .cRow
    cursor: pointer

  .cRow td
    padding-bottom: 0
    padding-top: 5px
    margin-top: 0
    margin-bottom: 0

.q-table__bottom--nodata
  position: absolute
  padding-top: 20px
  border-top: none !important

  font-size: 14px
  color: #777777

.query-dialog-scroll .q-table__bottom--nodata
  position: static


.q-field-content span
  white-space: pre-line


.q-btn.q-ml-xs, q-btn.q-mx-xs
  margin: 5px 4px 4px 0 !important

.text-xs
  font-size: 10px

.text-sm
  font-size: 15px

.text-md
  font-size: 20px

.text-lg
  font-size: 25px

.text-xl
  font-size: 30px

.fixedSpinner
  position: fixed

  top: 55%


.my-sticky-header-table
  /* height or max-height is important */
  max-width: 100%

  &:focus
    outline: none

  tr th
    position: sticky
    /* higher than z-index for td below */
    z-index: 2
    /* bg color: is important; just specify one */
    background: #FFFFFF

  thead tr:last-child th
    /* height of all previous header rows */
    top: 48px
    /* highest z-index */
    z-index: 3

  thead tr:first-child th
    top: 0
    z-index: 1

  tr:first-child th:first-child
    /* highest z-index */
    z-index: 3

.q-table__bottom
  //min-height 42px !important
  min-height: auto !important
  border-top: none
  padding: 0

  span
    font-size: 12px

.q-td
  overflow: hidden

// hacks!
.q-table--grid
  .q-table__grid-content
    overflow: auto

//.q-field--dense .q-field__control,
//.q-field--dense .q-field__marginal
//  height: 35px

.recnavi
  border-top: 1px solid #DDDDDD

// Column Styles for query results - free to use for query designers
.alert
  background-color: red
  color: white
  font-weight: bold
  text-align: center

.yellow
  background-color: #FFD600
  color: white
  font-weight: bold
  text-align: center

.blue
  background-color: #00AFE9
  color: white
  font-weight: bold
  text-align: center

.warning
  background-color: orange
  color: white
  font-weight: bold
  padding: 0 5px
  text-align: center

.ok
  background-color: green
  color: white
  font-weight: bold
  text-align: center

.fieldgroup
  background-color: unset !important
  padding-bottom: 0 !important

  .q-field__control-container
    padding-top: 10px !important

  .bg-grey-1
    background: none !important

  .q-field__label,
  .q-field__bottom
    display: none !important

  .q-field__control
    padding: 0 !important

  .q-field__native
    font-weight: bold
    color: #666666
    font-size: 15px

  .q-field__control::before
    border-bottom-style: none !important

  p
    margin: 0

.infoFacts b
  margin-bottom: 8px
  display: inline-block

.copyDlg
  -webkit-user-select: text

.desktopImgFullscreen
  height: 100%
  width: 100%

.desktopImgFSSection
  height: 90%

.blink
  .q-icon
    animation: blink-animation 1s steps(5, start) infinite
    -webkit-animation: blink-animation 1s steps(5, start) infinite

  @keyframes blink-animation
    to
      visibility: hidden


  @-webkit-keyframes blink-animation
    to
      visibility: hidden

#qryContent_\{56A4CD67-D9A7-11EC-AD62-309C23AC5997\},
#qryContent_\{B5D8DE2B-E00E-11EC-AD62-309C23AC5997\}
  .actions
    button:disabled
      display: none

    .actPrint:disabled + .actDownload:disabled
      display: block !important

#qryMenu_\{56A4CD67-D9A7-11EC-AD62-309C23AC5997\},
#qryMenu_\{56A4CD67-D9A7-11EC-AD62-309C23AC5997\}
  .disabled.actPrint,
  .disabled.actDownload
    display: none !important

.appMode
  .q-item__label + .q-item__label
    margin-top: 1px

.btnFabAction
  position: absolute
  bottom: 5vh
  right: 1vw

.btnNarrow
  padding: 4px 8px

.drag-ghost
  opacity: 0.2
//background: transparentize($primary, 0.2) !important

.table-header-drag-move
  transition: transform 0.5s

.cardContainer
  background-color: #777

// SELECT compact
.table-select-compact .q-field__control
  min-height: 24px !important
  height: 24px !important
  padding-left: 5px !important
  padding-right: 5px !important

.table-select-compact .q-field__native,
.table-select-compact .q-field__input
  padding: 0 !important
  min-height: 24px !important
  line-height: 24px !important

.table-select-compact .q-field__marginal
  height: 24px !important

.table-select-compact .q-icon
  font-size: 16px !important

.table-select-compact .q-field__append .q-spinner
  width: 16px
  height: 16px

// CHECKBOX compact
.table-checkbox-compact
  min-height: 18px !important
  padding: 0 !important

  .q-checkbox__inner
    width: 16px !important
    min-width: 16px !important
    height: 16px !important

  .q-checkbox__bg
    width: 16px !important
    height: 16px !important
    top: 0 !important
    left: 0 !important

  .q-checkbox__label
    padding-left: 4px !important
    line-height: 16px !important

  .q-focus-helper
    display: none !important

// INPUT compact
.table-input-compact
  min-height: 24px !important
  padding: 0 !important

  .q-field__control
    min-height: 24px !important
    height: 24px !important
    padding: 0 5px !important

  .q-field__marginal
    min-height: 24px !important
    height: 24px !important

  .q-field__control-container
    padding-top: 0 !important
    padding-bottom: 0 !important

  .q-field__native,
  .q-field__input
    min-height: 24px !important
    height: 24px !important
    line-height: 24px !important
    padding-top: 0 !important
    padding-bottom: 0 !important

  .q-field__append,
  .q-field__prepend
    padding-top: 0 !important
    padding-bottom: 0 !important

  input
    padding-top: 0 !important
    padding-bottom: 0 !important

  .q-icon
    font-size: 16px !important


// INPUT AUTOGROW
.table-textarea-compact
  padding: 0 !important

  .q-field__control
    min-height: 24px !important
    padding: 0 5px !important
    align-items: flex-start !important

  .q-field__marginal
    min-height: 24px !important

  .q-field__control-container
    padding-top: 0 !important
    padding-bottom: 0 !important

  .q-field__native
    min-height: 24px !important
    padding-top: 2px !important
    padding-bottom: 2px !important
    line-height: 1.2 !important
    resize: none

  .q-field__input
    min-height: 24px !important
    padding-top: 2px !important
    padding-bottom: 2px !important
    line-height: 1.2 !important

  textarea
    min-height: 24px !important
    padding-top: 2px !important
    padding-bottom: 2px !important
    line-height: 1.2 !important

  .q-field__append,
  .q-field__prepend
    padding-top: 0 !important
    padding-bottom: 0 !important
</style>
